/**
 * FilmPrintPage — DTF Film-Print station (Phase 50.01.3 / BLU-233).
 *
 * Flow (batch-first, distinct from DTG's scan-single-garment): scan/select a
 * DTF batch → list its DTF garments → "Prepare & Drop" each (filmPrint.prepareFilm
 * resolves the per-design file + per-unit QR, the agent drops it into Fiery's hot
 * folder) → each unit advances PRINTING → FILM_PRINTED, either automatically (the
 * agent's tolerant done-detector, drained + relayed here) OR via the prominent
 * manual "Mark Film Done" button (go-live-critical; not hard-gated on automation).
 *
 * Follows PRODUCTION_DESIGN.md: scanner-first, ≥48px targets, modal-level errors
 * with audio, semantic colour for state, one blocking modal at a time, scanner
 * auto-focus paused while a modal is open.
 */
import { useState, useCallback, useEffect, useRef, useMemo, forwardRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from '@/contexts/LocationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { QRScannerInput } from '@/features/catcher-station';
import { FilmLocationDirective } from '@/features/film-storage';
import { ProductionImageLightbox } from '@/components/production/ProductionImageLightbox';
import {
  ProductionPreviewStrip,
  buildPreviewSourceChain,
  buildProductionLightboxData,
  productionPreviewKey,
  type ProductionPreviewEntry,
  type ProductionPreviewKind,
} from '@/components/production/ProductionPreviewTile';
import { useProductionLightbox } from '@/components/production/useProductionLightbox';
import { useScanFeedback } from '@/hooks/useScanFeedback';
import { useScannerAutoFocus } from '@/hooks/useScannerAutoFocus';
import { ScanErrorModal } from '@/components/ui/scan-error-modal';
import { useFieryAgent, type FieryDoneEvent } from '../hooks/useFieryAgent';
import { useFilmDownload } from '../hooks/useFilmDownload';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { extractGarmentId } from '@/lib/qr-utils';
import { useTechAwareScanRouting } from '@/features/production-orders/hooks/useTechAwareScanRouting';
import { SCAN_ROUTE_GARMENT_PARAM, printTechnologyRoute } from '@/features/production-orders/utils/print-tech-routing';
import { getUserMessage, parseNotDtfUnitBlocked, parseFilmArtworkTransient } from '@/lib/trpc-error-utils';
import { formatOrderNumber, stripOrderNamePrefix } from '@threadheads/shared';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useTrackerClientOrg } from '@/features/production-tracker/hooks/useTrackerClientOrg';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { ClientOrgBadge } from '@/components/ClientOrgBadge';
import { playSuccessBeep } from '@/lib/sounds';
import { toast } from 'sonner';
import { useBackendHeartbeat } from '@/hooks/useBackendHeartbeat';
import { ConnectionStatusBanner } from '@/components/ui/connection-status-banner';
import {
  Layers, ArrowLeft, ArrowRight, CheckCircle, WifiOff, Loader2, Film, AlertCircle, Send, ScanLine, RotateCcw, X, ExternalLink,
  Download,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { runBulkTolerant } from '../utils/bulk-tolerant';
import {
  buildBulkDropSummary, makeOutcomeCollector,
  type BulkOutcomeCollector, type BulkUnitOutcome,
} from '../utils/bulk-drop-summary';
import { formatUnitLabel } from '../utils/format-unit-label';
import { filmDropArgs } from '../utils/film-file';
import {
  handlePrepareOutcome,
  classifyPrepareAttempt,
  decideNextAttempt,
  MAX_PREPARE_ATTEMPTS,
  PREPARE_RETRY_DELAY_MS,
  type PrepareAttemptClass,
} from '../utils/handle-prepare-outcome';
import { recordNonDelivery } from '../utils/record-non-delivery';
import { escalationCopyKeys } from '../utils/escalation-copy';
import { RedoReasonDialog } from '@/components/production/RedoReasonDialog';
import {
  PrintTechnologyMismatchDialog,
  type PrintTechnologySwitchAttempt,
} from '@/components/production/PrintTechnologyMismatchDialog';
import { usePrintTechnologyMismatch } from '@/components/production/usePrintTechnologyMismatch';
import { useFilmRedoReasons } from '@/components/production/FilmRedoDialog';
import {
  ARTWORK_PREVIEW_REFRESH_WINDOW_MS,
  artworkPreviewRefreshInterval,
} from '../utils/artwork-preview-refresh';

const DONE_POLL_MS = 4000;

/** Hold the plain "Fetching artwork…" label this long before adding the elapsed
 *  seconds. Under a few seconds a counter is just noise; past it, it is the only
 *  proof the station is still working. */
const ELAPSED_HINT_AFTER_MS = 5000;

/**
 * Seconds elapsed while `active`, or null before {@link ELAPSED_HINT_AFTER_MS}.
 *
 * BLU-818 item 1: the source-direct Printway fetch can take MINUTES from China and
 * the card showed a frozen "Preparing…" throughout — Brett assumed it had failed,
 * checked the RIP folder, and walked away before it landed. Elapsed time is the
 * honest signal here: we cannot observe the server's internal stages (there is no
 * subscription transport in this app), so a progress bar would be invented, but
 * "still working, 47s" is true and is what the operator actually needs.
 */
function useElapsedSeconds(active: boolean): number | null {
  const [seconds, setSeconds] = useState<number | null>(null);
  useEffect(() => {
    if (!active) {
      setSeconds(null);
      return;
    }
    const startedAt = Date.now();
    const tick = () => {
      const ms = Date.now() - startedAt;
      setSeconds(ms >= ELAPSED_HINT_AFTER_MS ? Math.floor(ms / 1000) : null);
    };
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return seconds;
}

type UnitFilmState = 'idle' | 'preparing' | 'printing' | 'filmPrinted' | 'error';

/** Phase 50.1 escalation record for ONE unit: the retry ladder ran out (or the
 * failure was tagged non-retryable) and the station filed an artwork issue on
 * the operator's behalf. Drives the honest amber panel on the card — the
 * operator still has Retry / Mark Film Done, but they are told the truth about
 * what happened and what the system is doing about it. */
interface ArtworkEscalation {
  /** How many prepare attempts were burned (1 when escalated immediately). */
  attempts: number;
  /** The backend accepted the report and a prod_issues row exists. */
  issueFiled: boolean;
  /** The backend also kicked its artwork re-fetch, so a later Retry may land. */
  healTriggered: boolean;
  /** The report itself failed — automation has given up AND could not tell
   *  anyone, so the card must say so out loud (never a silent swallow). */
  reportFailed: boolean;
  /** BLU-817: the customiser never attached a design to this order line, so there
   *  is nothing to fetch and no retry can succeed. Selects a different escalation
   *  line — the generic one tells the operator to tap Retry, which for this class is
   *  advice that wastes their time. */
  noArtworkAttached: boolean;
  /** Phase 92: this garment's shared-blank link is broken, so no film can be
   *  made and it must not print on the client's own rules. Selects the "set it
   *  aside" copy; known from the permanent refusal the prepare threw. */
  supplyBlocked: boolean;
}

/** Batch-status enum → localized friendly-label key (PRODUCTION_DESIGN §4 —
 * operators read a friendly stage, never the raw DB token). */
const BATCH_STATUS_LABEL_KEYS: Record<string, string> = {
  OPEN: 'filmPrint.batchStatus.open',
  PICKING: 'filmPrint.batchStatus.picking',
  PICKED: 'filmPrint.batchStatus.picked',
  CATCHING: 'filmPrint.batchStatus.catching',
  PACKING: 'filmPrint.batchStatus.packing',
  COMPLETED: 'filmPrint.batchStatus.completed',
  CANCELLED: 'filmPrint.batchStatus.cancelled',
};

/** A single DTF garment row as returned by the film-print router (frontend-side
 * derivation so we don't import a backend module type). */
type DtfGarmentForFilm = RouterOutputs['filmPrint']['getDtfGarmentsForBatch']['garments'][number];

/**
 * PURE. Film Print's two fixed one-source tiles, in this order, for one unit.
 *
 * ONE definition, read by the card AND by the batch-wide flat list, so the two
 * can never disagree about which tiles a unit has. They used to be two
 * hand-written lists with different emptiness rules: the card always emitted
 * both tiles and the lightbox only emitted the URLs it found, which is exactly
 * how a tile ends up opening its neighbour's image.
 *
 * Neither station policy applies here: an artwork thumb and a customer product
 * preview are separate pieces of evidence, so neither may ever stand in for the
 * other and both tiles stay single-source. An artwork thumb that has not
 * generated yet (it is made lazily by the preview-rehost cron) shows as
 * unavailable rather than borrowing the product preview.
 *
 * `resolution` is the only difference between the two readers: the card takes
 * the small webp, the full view takes full resolution with that same webp
 * painted behind it as the instant placeholder. Emptiness is unchanged either
 * way, because the full URL falls back to the thumb.
 */
function buildFilmUnitPreviewEntries(
  garment: Pick<DtfGarmentForFilm, 'artworkThumbUrl' | 'artworkFullUrl' | 'customPreviewUrl'>,
  resolution: 'thumb' | 'full',
): Array<ProductionPreviewEntry & { thumbUrl?: string | null }> {
  const artUrl =
    resolution === 'full' ? garment.artworkFullUrl ?? garment.artworkThumbUrl : garment.artworkThumbUrl;
  return [
    {
      kind: 'artwork',
      // The card's small webp carries the full-size as its twin for the hover
      // zoom; in the 'full' pass the twin equals the url and is dropped.
      sources: buildPreviewSourceChain([[artUrl, 'artwork', garment.artworkFullUrl]]),
      thumbUrl: resolution === 'full' ? garment.artworkThumbUrl : undefined,
    },
    {
      kind: 'productPreview',
      sources: buildPreviewSourceChain([[garment.customPreviewUrl, 'preview']]),
    },
  ];
}

/** Garment statuses across the whole PRE-CATCH film lifecycle — the window where
 * a film has been sent/printed and a redo is meaningful. Matches the backend
 * FILM_ONLY_REDOABLE_STATUSES: LABEL_PRINTED/PICKED (film sent to the RIP, "At
 * RIP", not yet marked done), FILM_PRINTED (marked done), and PRESSED (up to /
 * including the press). getDtfGarmentsForBatch also lists CAUGHT/PACKED (past the
 * film stage), which are excluded here. Paired with a "film actually sent" check
 * (`hasSentFilm`) so the button never shows on a not-yet-sent unit (§10 — no
 * doomed controls). */
const REDOABLE_GARMENT_STATUSES = new Set(['LABEL_PRINTED', 'PICKED', 'FILM_PRINTED', 'PRESSED']);

/** Garment statuses the batch-level "Redo all films" action sweeps. Narrower than
 * the per-unit set: bulk redoes films still AT this station (sent/printed, not yet
 * pressed) — LABEL_PRINTED/PICKED ("At RIP") + FILM_PRINTED — but NOT PRESSED
 * (already pressed onto a garment). A batch-wide tap must never scrap pressed
 * work; a single-unit redo still covers the PRESSED edge. */
const BULK_REDOABLE_GARMENT_STATUSES = new Set(['LABEL_PRINTED', 'PICKED', 'FILM_PRINTED']);

/** A garment the batch "Redo all films" sweep should include: still at the station
 * (not pressed/caught) AND a film has actually been sent (a non-defective film row
 * exists) — so the sweep only scraps real films, never a not-yet-sent unit. */
const isBulkRedoable = (g: DtfGarmentForFilm): boolean =>
  BULK_REDOABLE_GARMENT_STATUSES.has(g.status) && !!g.film && g.film.status !== 'DEFECTIVE';

export function FilmPrintPage() {
  const { t } = useTranslation('production');
  const { selectedLocation } = useLocation();
  const heartbeat = useBackendHeartbeat();
  const feedback = useScanFeedback();
  const agent = useFieryAgent();

  // Tech-aware scan routing (BLU-233): a DTG garment scanned here is never
  // filmed blind. This is the DTF Film-Print station, so stationTech='DTF'.
  // Only a DB-CONFIRMED tech decides — see handleScan below (2026-08-08
  // incident: the scanned label's `m` can go stale once a unit's DB
  // print_technology is corrected after the label was printed, so it can
  // never be trusted on its own). BLU-1509: a DB-confirmed DTG shirt opens the
  // mismatch dialog (Go to DTG printer / Do this DTF instead).
  const routing = useTechAwareScanRouting('DTF', feedback);
  const mismatch = usePrintTechnologyMismatch('DTG', routing);
  const { openMismatch, close: closeMismatch, closeSwitched } = mismatch;

  // Incoming ?garment=<id> context from a cross-tech redirect off the DTG print
  // station — consumed below to focus the unit if it's in the active batch.
  const [searchParams, setSearchParams] = useSearchParams();
  // Empty-state CTA only (the cross-tech scan bounce owns its own navigation).
  const emptyStateNavigate = useNavigate();

  // Selected batch (by id).
  const [batchId, setBatchId] = useState<string | null>(null);
  const [artworkRefreshDeadline, setArtworkRefreshDeadline] = useState(0);
  useEffect(() => {
    setArtworkRefreshDeadline(
      batchId && selectedLocation?.id
        ? Date.now() + ARTWORK_PREVIEW_REFRESH_WINDOW_MS
        : 0,
    );
  }, [batchId, selectedLocation?.id]);

  // BLU-818 item 3: order number to pre-fill the in-batch search with, handed over by
  // whichever path is about to open a batch from an order search. Consumed (and
  // cleared) by the `[batchId]` effect below, so it survives exactly one batch change
  // and can never re-apply after the operator clears the field themselves.
  const pendingOrderSearchRef = useRef<string | null>(null);
  /** Open a batch carrying the searched order into its film list. Empty input degrades
   *  to the previous behaviour: open the batch with a clean search. */
  const handOffOrderSearch = useCallback((orderNumber: string) => {
    pendingOrderSearchRef.current = orderNumber || null;
  }, []);

  // Order-number search (260818-7oj). IN-BATCH: narrows the visible garment
  // cards to the typed order (tolerant of the '#' prefix and case).
  // NO-BATCH: submitting resolves the DTF batch(es) containing that order at
  // the selected location via a SERVER query (never a client-side filter over
  // the paginated `listDtfBatches` page — CLAUDE.md §10 filter-layer rule).
  const [orderSearch, setOrderSearch] = useState('');
  const [crossBatchMatches, setCrossBatchMatches] = useState<
    { batchId: string; batchNumber: number | null; status: string }[] | null
  >(null);
  const [crossBatchSearching, setCrossBatchSearching] = useState(false);
  // Stale-response guard for handleCrossBatchSearch (item 8, 6-lens review).
  const crossBatchSearchTokenRef = useRef(0);

  // Per-garment client state (keyed by garmentUnitId).
  const [unitStates, setUnitStates] = useState<Record<string, UnitFilmState>>({});

  // BLU-233 pre-printed: garments whose film was auto-claimed from PRE-PRINTED
  // shelf stock (no Fiery drop). Maps garmentUnitId → the shelf slot label the
  // claimed film sits in ('' when the claim returned no slot). Drives the
  // "pulled from shelf" retrieval cue on the row.
  const [shelfClaims, setShelfClaims] = useState<Record<string, string>>({});

  // "Not sent" persistence (6-lens review item 6, batch 1879/1717): a unit
  // whose drop failed/was-skipped still ends up in `unitStates: 'printing'`
  // (the same "At RIP" pill a GOOD drop shows) so the card looks identical to
  // a real success once the run's toast summary closes. Tracks every unit
  // still awaiting a manual print, independent of `unitStates`, so the card
  // can render a persistent amber cue until the unit is successfully
  // re-sent (or the operator resolves it another way).
  const [needsManualIds, setNeedsManualIds] = useState<Set<string>>(new Set());
  const markNeedsManual = useCallback((garmentUnitId: string) => {
    setNeedsManualIds((s) => (s.has(garmentUnitId) ? s : new Set(s).add(garmentUnitId)));
  }, []);
  const clearNeedsManual = useCallback((garmentUnitId: string) => {
    setNeedsManualIds((s) => {
      if (!s.has(garmentUnitId)) return s;
      const next = new Set(s);
      next.delete(garmentUnitId);
      return next;
    });
  }, []);
  // Read by the seed effect (blocker A) so a refetch can't repaint a
  // never-delivered unit as "At RIP" — kept in a ref rather than the effect's
  // deps so flagging a unit doesn't re-run the seed pass.
  const needsManualRef = useRef(needsManualIds);
  useEffect(() => { needsManualRef.current = needsManualIds; }, [needsManualIds]);

  // ── Retry ladder (phase 50.1 post-impl review) ────────────────────────────
  // A prepare attempt that delivered NOTHING to the RIP for a retryable reason
  // (combined-composite failure, a transient artwork/storage incident, or a
  // proven-absent file whose mirror-heal the backend just fired) is retried
  // automatically up to MAX_PREPARE_ATTEMPTS times, ~4s apart, with the
  // progress on the unit's own card — never a page-blocking gate.
  //
  // `prepareAttemptsRef` is the in-memory attempt counter (per garment, per
  // session): it survives re-renders, resets when the unit is successfully
  // sent / redone / the batch or location changes, and is NEVER persisted —
  // a fresh shift starts with a fresh ladder.
  const prepareAttemptsRef = useRef<Record<string, number>>({});
  // 1-based attempt currently in flight for a unit, when > 1. Drives the
  // "Retrying (2/3)…" busy label; absent for a first attempt (plain
  // "Preparing…"), so a normal send looks exactly as it always has.
  const [retryAttempts, setRetryAttempts] = useState<Record<string, number>>({});
  const [escalations, setEscalations] = useState<Record<string, ArtworkEscalation>>({});
  // Claimed source density per side, from the last prepare (Brett 2026-08-19):
  // 300 DPI is the DTF standard — any other value gets a badge on the card;
  // under 300 warns "print may be blurry". Informational, session-local.
  const [unitDpi, setUnitDpi] = useState<Record<string, { front: number | null; back: number | null }>>({});
  /** Drop just the "Retrying (n/3)…" label — the ladder is no longer counting
   *  for this unit. Does NOT touch the escalation panel (a settled ladder that
   *  gave up still has something to say). */
  const clearRetryLabel = useCallback((garmentUnitId: string) => {
    setRetryAttempts((s) => {
      if (!(garmentUnitId in s)) return s;
      const next = { ...s };
      delete next[garmentUnitId];
      return next;
    });
  }, []);
  /** Full reset for a unit: attempt counter, retry label and escalation panel.
   *  Called whenever the unit reaches a genuinely good state (drop succeeded,
   *  shelf-claimed, marked done, redone) or leaves this session's scope. */
  const clearRetryState = useCallback((garmentUnitId: string) => {
    delete prepareAttemptsRef.current[garmentUnitId];
    clearRetryLabel(garmentUnitId);
    setEscalations((s) => {
      if (!(garmentUnitId in s)) return s;
      const next = { ...s };
      delete next[garmentUnitId];
      return next;
    });
  }, [clearRetryLabel]);

  // Bulk-run state (batch-first UX). `kind` drives the progress label; null when
  // idle. progress is 1-based "done/total" while a bulk loop is in flight. An
  // in-flight guard (the truthiness of `bulkRun`) plus `bulkRunningRef` stops the
  // bulk buttons double-firing (a slow loop + a second tap must not overlap).
  const [bulkRun, setBulkRun] = useState<
    { kind: 'prepare' | 'resend' | 'markDone' | 'redo'; done: number; total: number } | null
  >(null);
  const bulkRunningRef = useRef(false);

  // Error tracking (mirror PrintStationPage/CatcherPage).
  const hasScannedRef = useRef(false);
  const shownErrorRef = useRef<string | null>(null);
  // In-flight guard for the scanner-first "scan a garment to open its batch"
  // resolve so a slow lookup + a second scan can't double-fire.
  const resolvingGarmentScanRef = useRef(false);

  // Open batches at this location to pick from: DTF-bearing customer batches
  // only (a batch with no DTF garments has nothing to do at this station).
  // filmPrint.listDtfBatches forces the DTF filter, the customer type and the
  // newest-first order server-side, and filters, counts and pages in one query.
  const batchesQuery = trpc.filmPrint.listDtfBatches.useQuery(
    {
      locationId: selectedLocation?.id ?? '',
      statuses: ['OPEN', 'PICKING', 'PICKED', 'CATCHING'],
      limit: 25,
      offset: 0,
    },
    { enabled: !!selectedLocation?.id, staleTime: 30_000 },
  );

  // DTF garments for the selected batch.
  const garmentsQuery = trpc.filmPrint.getDtfGarmentsForBatch.useQuery(
    { batchId: batchId ?? '', locationId: selectedLocation?.id ?? '' },
    {
      enabled: !!batchId && !!selectedLocation?.id,
      staleTime: 10_000,
      // The durable worker normally finishes before the operator reaches Film.
      // If not, refresh only while this newly-opened batch has a missing tile,
      // then stop on success or after two minutes. No browser state is persisted.
      refetchInterval: (query) => artworkPreviewRefreshInterval(
        query.state.data,
        artworkRefreshDeadline,
      ),
    },
  );

  // gcTime 0: a prepared film payload (up to ~4.7 MB of base64) must not sit in
  // the mutation cache after the call; Download film runs its own prepare.
  const prepareMutation = trpc.filmPrint.prepareFilm.useMutation({ gcTime: 0 });
  const markFilmPrintedMutation = trpc.filmPrint.markFilmPrinted.useMutation();
  const reportDropOutcomeMutation = trpc.filmPrint.reportDropOutcome.useMutation();
  // Phase 50.1 escalation: files a prod_issues row (and kicks the backend's
  // artwork re-fetch) once the retry ladder has given up on a unit. This is the
  // ONLY place the station reports on the operator's behalf — automation tells
  // someone exactly when it stops being able to help.
  const reportArtworkIssueMutation = trpc.filmPrint.reportArtworkIssue.useMutation();
  // Film redo goes through filmPrint.redoFilm, which forces the film-only
  // subtype and runs the same catcher redo path: the garment goes back to
  // PICKED, the film is defected, its slot released and the reason recorded (a
  // shelf-claimed film becomes DEFECTIVE, not returned to stock). It checks the
  // garment is at this floor and is open to every Film Printing role.
  const redoFilmMutation = trpc.filmPrint.redoFilm.useMutation();
  // BLU-1509: the safe in-place DTG to DTF flip (the server refuses anything else).
  const switchToDtfMutation = trpc.filmPrint.switchToDtf.useMutation();
  const switchToDtf = switchToDtfMutation.mutateAsync;
  const utils = trpc.useUtils();

  // Redo-film confirm dialog: the garment whose film we're scrapping + re-sending
  // (null = closed). A blocking modal (§6.1) — pauses the scanner while open.
  const [redoDialogGarmentId, setRedoDialogGarmentId] = useState<string | null>(null);
  // Bulk "Redo all films" confirm dialog (batch-scoped). Separate state from the
  // single-unit redo so each RedoReasonDialog carries its own copy; also pauses
  // the scanner while open.
  const [bulkRedoOpen, setBulkRedoOpen] = useState(false);

  // Fixed DTF-film redo reasons — shared hook (single authority for the set).
  const filmRedoReasons = useFilmRedoReasons();

  // Stable refs for the auto-done poll effect (Fix 1). The tRPC mutation object
  // is unstable (new identity every render); capturing it in a ref keeps the
  // poll effect's deps stable so the 4s setInterval is NOT torn down + recreated
  // every render (which silently broke auto-done detection). Same class as the
  // documented useScanFeedback unstable-object gotcha.
  const markMutationRef = useRef(markFilmPrintedMutation);
  useEffect(() => { markMutationRef.current = markFilmPrintedMutation; });
  // Ref-capture the prepare mutation too (same unstable-identity reason) so the
  // bulk-prepare loop can read it without putting the mutation object in deps.
  const prepareMutationRef = useRef(prepareMutation);
  useEffect(() => { prepareMutationRef.current = prepareMutation; });

  const reportDropOutcomeRef = useRef(reportDropOutcomeMutation);
  useEffect(() => { reportDropOutcomeRef.current = reportDropOutcomeMutation; });
  const reportArtworkIssueRef = useRef(reportArtworkIssueMutation);
  useEffect(() => { reportArtworkIssueRef.current = reportArtworkIssueMutation; });
  // Ref-capture the agent so the bulk-prepare loop drops files without making the
  // bulk callback depend on the (memoized but per-tick-changing) agent object.
  const agentRef = useRef(agent);
  useEffect(() => { agentRef.current = agent; });

  // knownIds for "is this done-event's garment in the current batch?" — derived
  // from a ref updated in a separate effect so the poll effect doesn't depend on
  // the (per-render) query data object.
  const knownIdsRef = useRef<Set<string>>(new Set());
  // In-flight guard: overlapping ticks (or a slow relay) must not double-relay
  // the same garment to markFilmPrinted.
  const relayingRef = useRef<Set<string>>(new Set());
  const selectedLocationRef = useRef(selectedLocation?.id ?? null);
  useEffect(() => { selectedLocationRef.current = selectedLocation?.id ?? null; });
  // feedback + t are read via refs by the stable bulk-loop cores so the bulk
  // callbacks keep empty/stable deps (feedback is never memoized — the
  // documented useScanFeedback gotcha; t can change on language switch).
  const feedbackRef = useRef(feedback);
  useEffect(() => { feedbackRef.current = feedback; });
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; });
  // utils has a stable tRPC identity, but the prepare core keeps [] deps (reads
  // everything via refs); read utils through a ref so the core can invalidate
  // after a shelf-claim without widening its deps.
  const utilsRef = useRef(utils);
  useEffect(() => { utilsRef.current = utils; });
  // "Download film" on a needs-manual card: the hook owns the single-flight
  // guard and the button state; "no film" also refetches the batch's garments.
  const {
    download: downloadFilmFile,
    isDownloading: isDownloadingFilm,
    reset: resetFilmDownloads,
  } = useFilmDownload({
    onWarn: feedback.triggerWarning,
    onUnavailable: () => void utils.filmPrint.getDtfGarmentsForBatch.invalidate(),
  });
  // Latest garments + per-unit states for the stable bulk loops (so the bulk
  // callbacks read fresh data without depending on it → stable identity, and the
  // loop can't double-fire from a stale closure). Synced in effects below.
  // Keyed by garmentUnitId: every reader looks ONE unit up, often once per unit
  // inside a bulk loop, and a find() there is quadratic on a big batch.
  const garmentsByIdRef = useRef<Map<string, DtfGarmentForFilm>>(new Map());
  // Mirrors `filteredGarments` (the cards actually on screen) — the bulk
  // loops sweep THIS set, never the whole-batch list, so an active order-number search
  // narrows bulk actions to the same cards it narrows the view to.
  const filteredGarmentsRef = useRef<DtfGarmentForFilm[]>([]);
  const unitStatesRef = useRef<Record<string, UnitFilmState>>({});
  useEffect(() => { unitStatesRef.current = unitStates; }, [unitStates]);

  // Seed per-unit state from the server (existing film rows). FILM_PRINTED
  // garment status OR a STORED/PRESSED film = done; a PRINTING film = printing.
  //
  // BLOCKER A (phase 50.1 post-impl review): the seed pass used to overwrite
  // EVERY non-preparing unit, so a unit whose film never reached the RIP was
  // repainted `'printing'` — the identical "At RIP" pill a real success shows —
  // on the very next refetch, and the batch-level "Mark all done" then swept it
  // to FILM_PRINTED with no film in existence. A unit this session knows
  // nothing was delivered for (state `'error'`, or flagged needs-manual) is
  // therefore left alone. The one thing that still wins over that guard is a
  // server-side FILM_PRINTED/STORED/PRESSED film: that is authoritative
  // progress (auto-relay, manual mark-done, or another station), never a
  // downgrade, so it promotes the card and drops the stale amber cue.
  useEffect(() => {
    const garments = garmentsQuery.data?.garments;
    if (!garments) return;
    // Keep the poll effect's "is this garment in the current batch?" set fresh
    // without putting the per-render query data in the poll effect's deps (Fix 1).
    knownIdsRef.current = new Set(garments.map((g) => g.garmentUnitId));
    const promoted: string[] = [];
    setUnitStates((prev) => {
      const next = { ...prev };
      for (const g of garments) {
        const current = next[g.garmentUnitId];
        if (current === 'preparing') continue; // don't clobber an in-flight prep
        const serverDone =
          g.status === 'FILM_PRINTED' || g.film?.status === 'STORED' || g.film?.status === 'PRESSED';
        if (serverDone) {
          if (current !== 'filmPrinted') promoted.push(g.garmentUnitId);
          next[g.garmentUnitId] = 'filmPrinted';
          continue;
        }
        // Nothing was delivered for this unit in THIS session — never repaint it
        // "At RIP" (or back to idle) off a refetch.
        if (current === 'error' || needsManualRef.current.has(g.garmentUnitId)) continue;
        next[g.garmentUnitId] = g.film?.status === 'PRINTING' ? 'printing' : 'idle';
      }
      return next;
    });
    // A server-confirmed done film retires this session's "not sent" cue +
    // escalation panel — otherwise a done card would contradict itself
    // ("Film Printed" beside "Not sent — print manually").
    for (const id of promoted) {
      clearNeedsManual(id);
      clearRetryState(id);
    }
  }, [garmentsQuery.data, clearNeedsManual, clearRetryState]);

  // Relay an agent done event to the backend (single writer = markFilmPrinted).
  // Stable identity (deps = only the stable `utils`): reads the location +
  // mutation via refs and guards against double-relaying the same garment so an
  // overlapping poll tick can't fire markFilmPrinted twice for one unit (Fix 1).
  const relayDone = useCallback(async (garmentUnitId: string) => {
    const locationId = selectedLocationRef.current;
    if (!locationId) return;
    if (relayingRef.current.has(garmentUnitId)) return; // already in flight
    relayingRef.current.add(garmentUnitId);
    try {
      const result = await markMutationRef.current.mutateAsync({ garmentUnitId, locationId });
      if (result.success) {
        setUnitStates((s) => ({ ...s, [garmentUnitId]: 'filmPrinted' }));
        // The film is done server-side — a stale "not sent" cue or escalation
        // panel beside a "Film Printed" pill would contradict itself.
        clearNeedsManual(garmentUnitId);
        clearRetryState(garmentUnitId);
        playSuccessBeep();
        void utils.filmPrint.getDtfGarmentsForBatch.invalidate();
      }
    } catch (error) {
      // A relay failure is non-fatal — the manual-done button is the fallback.
      console.warn('[film-print] markFilmPrinted relay failed:', getUserMessage(error));
    } finally {
      relayingRef.current.delete(garmentUnitId);
    }
  }, [utils, clearNeedsManual, clearRetryState]);

  // Poll the agent's done-queue while a batch is active + the agent has DTF.
  // Each drained event whose garment is in THIS batch is relayed to
  // markFilmPrinted. An agent that serves /film/drop has no done-queue, so no
  // timer starts for it. Deps are ALL stable now (Fix 1): `batchId` (primitive),
  // `agent.hasDtf` / `agent.hasFilmRoute` (primitives), `agent.drainDone`
  // (useCallback, agent is memoized), and `relayDone` (stable). The interval is
  // created once per batch selection instead of being torn down + recreated
  // every render — which is what silently broke auto-done detection before.
  // knownIds is read from a ref so the set stays fresh without re-running this
  // effect.
  const drainDone = agent.drainDone;
  const hasDtf = agent.hasDtf;
  const hasFilmRoute = agent.hasFilmRoute;
  useEffect(() => {
    if (!batchId || !hasDtf || hasFilmRoute) return;
    let cancelled = false;
    const tick = async () => {
      const events: FieryDoneEvent[] = await drainDone();
      if (cancelled) return;
      for (const ev of events) {
        if (ev.garmentUnitId && knownIdsRef.current.has(ev.garmentUnitId)) {
          await relayDone(ev.garmentUnitId);
        } else if (!ev.garmentUnitId) {
          // BLU-233 Track A: a done event with NO recoverable garment id means
          // the Fiery output filename dropped our `<uuid>__` prefix — auto-relay
          // silently degrades to manual for that film. Surface it loudly so the
          // naming convention gets fixed instead of quietly rotting.
          console.warn('[film-print] Fiery done event with no garment id — auto-relay skipped', ev);
          toast.warning(t('filmPrint.doneEventNoGarment'), {
            description: t('filmPrint.doneEventNoGarmentDesc', { file: ev.filePath.split(/[\\/]/).pop() ?? ev.filePath }),
            duration: 8000,
          });
        }
      }
    };
    const interval = setInterval(() => void tick(), DONE_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
    // `t` intentionally excluded — i18next's t is stable in practice but not
    // referentially; including it would re-create the poll interval per render
    // (the exact bug Fix 1 removed).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, hasDtf, hasFilmRoute, drainDone, relayDone]);

  // Clear state on location change. orderSearch is cleared DIRECTLY here
  // (not left to the `[batchId]` effect below) — when batchId is ALREADY
  // null (no batch open), `setBatchId(null)` is a same-value no-op and React
  // never re-fires the `[batchId]` effect, so a stale order search would
  // otherwise survive the location switch (item 8, 6-lens review).
  useEffect(() => {
    setBatchId(null);
    setOrderSearch('');
    // A handoff queued moments before the location switch belongs to the OLD
    // location's batch — drop it rather than letting the `[batchId]` effect apply it.
    pendingOrderSearchRef.current = null;
    setUnitStates({});
    setShelfClaims({});
    setNeedsManualIds(new Set());
    resetFilmDownloads();
    setRetryAttempts({});
    setEscalations({});
    prepareAttemptsRef.current = {};
    setCrossBatchMatches(null);
    hasScannedRef.current = false;
    shownErrorRef.current = null;
    knownIdsRef.current = new Set();
    relayingRef.current = new Set();
    closeMismatch();
  }, [selectedLocation?.id, resetFilmDownloads, closeMismatch]);

  // Clear the in-batch search whenever the OPEN batch changes (new batch
  // selected, or back to the batch list) — a stale query must never silently
  // narrow the next batch's cards. Also clears any stale cross-batch match
  // list (item 8) — the two searches are separate UIs, but a query typed
  // moments before opening a batch must not leave a stale pick-list behind.
  useEffect(() => {
    // BLU-818 item 3: EXCEPT when a handoff is pending for the batch being opened —
    // searching an order and then opening its batch must land with that search still
    // applied, not wiped by this effect firing on the batch change.
    //
    // The pending value is a REF, not a URL param. A param would round-trip through
    // `searchParams`, which would have to join this effect's deps — and then STRIPPING
    // the param (needed so a re-render can't undo a manual clear) re-fires the effect
    // with the param already gone, wiping the very search it just applied. A ref has
    // no such ordering hazard, and this handoff never crosses a page boundary the way
    // the cross-tech `?garment=` one does.
    const pending = pendingOrderSearchRef.current;
    pendingOrderSearchRef.current = null;
    setOrderSearch(pending ?? '');
    setCrossBatchMatches(null);
  }, [batchId]);

  // BLU-233: consume the ?garment=<id> context handed over by a cross-tech
  // redirect from the DTG print station. The Film-Print station is batch-first,
  // so we can't resolve a bare garment to its batch up front — we stash the id
  // and highlight the matching unit IF/when its batch is opened, then strip the
  // param so it doesn't re-fire. Graceful no-op if the garment is never opened.
  const [focusGarmentId, setFocusGarmentId] = useState<string | null>(null);
  const consumedGarmentParamRef = useRef<string | null>(null);
  useEffect(() => {
    const garmentParam = searchParams.get(SCAN_ROUTE_GARMENT_PARAM);
    if (!garmentParam) return;
    if (consumedGarmentParamRef.current === garmentParam) return;
    consumedGarmentParamRef.current = garmentParam;
    setFocusGarmentId(garmentParam);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(SCAN_ROUTE_GARMENT_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // Ref to the highlighted unit's card (set below when the matching card renders).
  const focusCardRef = useRef<HTMLDivElement | null>(null);

  // Stable CORE: prepare + drop ONE unit's film — exactly ONE attempt. Reads
  // location/mutation/agent/t via refs so the retry ladder + bulk loop (below)
  // can reuse it without unstable deps. THROWS on a prepare failure (so the
  // ladder can classify it and the bulk runner can still record it).
  //
  // It no longer touches the bulk outcome collector: it REPORTS one attempt
  // (`bucket` + `lines` + `attemptClass`) and the retry ladder above it records
  // exactly ONE outcome per unit into the run. That split is what keeps
  // `buildBulkDropSummary`'s reconciliation invariant true when a unit is
  // retried — an intermediate attempt's `failed` must never be counted
  // alongside the final attempt's `dropped`.
  //
  // `opts.raiseWarningModals` (single-unit runs) shows warning-severity findings
  // as blocking modals immediately, as before. ERROR-severity findings (nothing
  // reached the RIP) never open a modal from here: the ladder owns that voice,
  // so one film can't interrupt the operator once per attempt
  // (PRODUCTION_DESIGN §6.1 — one blocking modal at a time).
  const prepareAndDropUnitCore = useCallback(
    async (
      garmentUnitId: string,
      orderNumber: string,
      opts?: { raiseWarningModals?: boolean },
    ): Promise<{ attemptClass: PrepareAttemptClass; bucket: BulkUnitOutcome; lines: string[] }> => {
      const raiseWarningModals = opts?.raiseWarningModals ?? false;
      const locationId = selectedLocationRef.current;
      if (!locationId) throw new Error('No location selected');
      setUnitStates((s) => ({ ...s, [garmentUnitId]: 'preparing' }));
      const translate = tRef.current;
      // Every warning line recorded for THIS unit — returned as a list so
      // blank-artwork, headerless, and drop-failure warnings for the SAME unit
      // are never lost by only keeping the last one.
      const unitLines: string[] = [];
      // Per-film label so a bulk summary can say WHICH film needs manual
      // attention (order + SKU), not just how many — computed LAZILY (only
      // once a warning actually fires), since a clean drop never needs it.
      let unitLabel: string | null = null;
      const warn = (titleKey: string, msg: string, severity: 'error' | 'warning' = 'warning') => {
        if (unitLabel === null) {
          const unit = garmentsByIdRef.current.get(garmentUnitId);
          unitLabel = formatUnitLabel({ orderNumber, shopifySku: unit?.shopifySku, sku: unit?.sku });
        }
        unitLines.push(`${unitLabel}: ${msg}`);
        if (!raiseWarningModals || severity === 'error') return;
        feedbackRef.current.triggerWarning(translate(titleKey), msg);
      };
      try {
        const prep = await prepareMutationRef.current.mutateAsync({
          garmentUnitId,
          locationId,
          orderNumber,
          printTechnology: 'DTF',
          fulfillmentType: 'POD',
        });

        // ORDERING (post-impl review item C — TDZ footgun): `dropArgs` is
        // declared HERE, above `reportOutcome`, because `reportOutcome` closes
        // over it for its filename fallback and the early-return branches below
        // call `reportOutcome` before the drop branch ever runs. Declared after
        // `reportOutcome`, that read sat in `dropArgs`' temporal dead zone and
        // only stayed safe by accident (every early caller happened to pass an
        // explicit filename) — one future caller that didn't would have thrown
        // a ReferenceError mid-prepare. `let` + `null` init keeps the drop
        // branch's assignment unchanged.
        //
        // #1044: prefer the artwork with the tile-margin IDENTITY HEADER
        // composited on (QR + Order#/Design Title/Side/blank garment) — drop
        // those bytes directly. Fall back to the raw presigned URL when the
        // header could not be composited (non-raster artwork, `separate_label`
        // strategy, no source URL e.g. Azure, or a compositor error).
        let dropArgs: { filename: string; bytesBase64?: string; presignedUrl?: string } | null = null;

        // Best-effort server-side audit of the drop outcome (FILM_DROP_OK /
        // FILM_DROP_FAILED prod_events) — the drop happens browser→local
        // agent, so without this relay a failure is invisible server-side
        // (prod batch 1672). Fire-and-forget; never blocks the station.
        // Hoisted above BOTH early-return branches below (shelf-claim,
        // combined-composite-failure) so every "nothing reached the RIP"
        // exit — not just a real drop attempt — can call it. `filename` is
        // parameterised: the agent-drop call sites further down omit it and
        // keep the pre-existing `dropArgs?.filename ?? prep.filename ?? null`
        // fallback (unchanged behavior); the non-delivery exits pass their own
        // filename explicitly.
        const reportOutcome = (success: boolean, error?: string, destPath?: string, filename?: string | null) => {
          reportDropOutcomeRef.current
            .mutateAsync({
              garmentUnitId,
              locationId,
              success,
              filename: filename !== undefined ? filename : (dropArgs?.filename ?? prep.filename ?? null),
              destPath: destPath ?? null,
              error: error ? error.slice(0, 500) : null,
            })
            .catch(() => undefined);
        };

        // The ONE way this station records "nothing reached the RIP" for this
        // unit: audit + operator warning + the persistent needs-manual flag,
        // together, always (see `record-non-delivery.ts`). Bound to this
        // garment + this attempt's channels once, then used by all five exits.
        const nonDelivery = (spec: Parameters<typeof recordNonDelivery>[1]) =>
          recordNonDelivery(garmentUnitId, spec, {
            reportOutcome,
            markNeedsManual,
            warn,
            log: (line) => console.warn(line),
          });

        // Two early-return outcomes — shelf-claim (BLU-233 pre-printed) and
        // the plan 04 combined-film fail-closed gate — are decided by the
        // extracted, unit-tested `handlePrepareOutcome` (see
        // `film-print-drop-outcome.test.ts`). This function only sets the
        // unit's state and (for the combined failure) records the
        // non-delivery; the component still owns the shelf-claim bookkeeping
        // below, matching its pre-extraction behaviour.
        const outcome = handlePrepareOutcome(prep, {
          setUnitState: (state) => setUnitStates((s) => ({ ...s, [garmentUnitId]: state })),
          recordNonDelivery: nonDelivery,
          translate,
        });

        // BLU-233 pre-printed: prepareFilm auto-claimed a matching PRE-PRINTED
        // shelf film for this garment — it is already FILM_PRINTED server-side
        // and NOTHING drops to Fiery (presignedUrl/composited* are null). Record
        // the shelf slot for the retrieval cue, refresh the list, and return
        // BEFORE the agent-drop branch below (state already set above).
        if (outcome.kind === 'shelfClaim') {
          setShelfClaims((s) => ({ ...s, [garmentUnitId]: prep.shelfSlotLabel ?? '' }));
          clearNeedsManual(garmentUnitId);
          clearRetryState(garmentUnitId);
          void utilsRef.current.filmPrint.getDtfGarmentsForBatch.invalidate();
          return { attemptClass: outcome.attemptClass, bucket: 'shelfClaimed', lines: unitLines };
        }

        // COMBINED-FILM FAIL-CLOSED (plan 04): audited, flagged needs-manual,
        // warned and state-set by `handlePrepareOutcome` above (via the shared
        // non-delivery triple); its warning line rides in `unitLines` like every
        // other exit's, so the bulk reconciliation summary sees it.
        if (outcome.kind === 'combinedFailure') {
          return { attemptClass: outcome.attemptClass, bucket: 'failed', lines: unitLines };
        }

        // Which reconciliation bucket this attempt lands in — set by the
        // branches below and returned to the ladder, which records it ONCE.
        let bucket: BulkUnitOutcome = 'failed';

        // Drop the prepared file via the agent (best-effort — the film row is
        // already PRINTING server-side; a drop failure leaves the manual path).
        // The backend mints a short-lived presigned GET URL for the artwork; the
        // agent fetches the bytes from it. When no URL could be produced (e.g.
        // Azure legacy backend), skip the auto-drop and keep the manual path.
        const liveAgent = agentRef.current;
        // BLU-233: prefix the drop filename with the garment unit id so (a) the
        // agent's done-detector can recover the id from the consumed file
        // (parseGarmentIdFromFilename reads the `<uuid>__<name>` form) → auto-
        // relay works, not just manual; and (b) two DTF garments sharing an
        // artwork SKU don't drop the SAME basename into the flat hot folder
        // (collision + the basename-keyed dedup would fire one done event for
        // both). MZ is ~90% personalised DTF, so SKU-name collisions are real.
        // (`dropArgs` itself is declared above `reportOutcome` — see the
        // ordering note there.)
        if (liveAgent.hasDtf) {
          // One builder for both film pages and for Download film (film-file.ts):
          // composited bytes, else the presigned URL (composited name when the
          // oversize composite was uploaded), else null.
          dropArgs = filmDropArgs(garmentUnitId, prep);

          // headerless = we fell back to the RAW artwork drop (no identity header
          // composited). The film looks identical to a good one until the cut
          // step, so warn the operator to match it manually. In a bulk run the
          // warning is collected instead of shown as a modal.
          // presignedComposited = the presigned file already carries the
          // baked header (oversize-composite upload) — NOT headerless.
          const headerless = dropArgs != null && dropArgs.bytesBase64 == null && !prep.presignedComposited;

          // BLANK-ARTWORK warning (batch 1690): the backend detected an
          // effectively all-white opaque source — the film would print a
          // header with nothing under it. Warn BEFORE the operator prints.
          // Never fatal to the drop itself (still counts as `dropped` below).
          // Post-impl nit: `blankSides` names WHICH side(s) looked blank on a
          // dual/back-only film, so the operator checks the right design
          // instead of both; the unsided copy stays as the fallback.
          // DPI badge data (never blocks anything): remember each side's
          // claimed density so the card can flag non-300-DPI files.
          if (prep.artworkDpi) {
            const dpi = prep.artworkDpi;
            setUnitDpi((s) => ({ ...s, [garmentUnitId]: dpi }));
          }
          if (prep.artworkLooksBlank) {
            const sides = prep.blankSides ?? [];
            const sideLabel =
              sides.length === 2
                ? translate('filmPrint.sideBoth')
                : sides[0] === 'back'
                  ? translate('filmPrint.sideBack')
                  : sides[0] === 'front'
                    ? translate('filmPrint.sideFront')
                    : null;
            warn(
              'filmPrint.blankArtworkTitle',
              sideLabel
                ? translate('filmPrint.blankArtworkBodySides', { sides: sideLabel })
                : translate('filmPrint.blankArtworkBody'),
            );
          }

          // Backend surfaced a PROVEN-absent artwork (never a URL we know
          // 404s — Task 1) — nothing to drop, and no source-direct fallback
          // recovered it either. Skip the drop attempt entirely; this counts
          // as `failed` (nothing reached the RIP), not a silent success.
          // RETRYABLE: the backend fires its confirmed-absence mirror-heal on
          // every miss, so the next attempt often finds the file that heal
          // just wrote (batch 1688's "PNG not mirrored yet" shape).
          if (prep.artworkMissing) {
            nonDelivery({
              auditReason: 'artwork absent from storage',
              warnTitleKey: 'filmPrint.artworkMissingTitle',
              warnMessage: translate('filmPrint.artworkMissingBody'),
              severity: 'error',
              logKey: 'artwork-missing',
            });
            setUnitStates((s) => ({ ...s, [garmentUnitId]: 'printing' }));
            return { attemptClass: 'retryable', bucket: 'failed', lines: unitLines };
          }

          let dropSucceeded = false;
          if (dropArgs) {
            const drop = await liveAgent.drop(dropArgs);
            if (!drop.success) {
              nonDelivery({
                auditReason: drop.error ?? 'unknown',
                destPath: drop.destPath,
                warnTitleKey: 'filmPrint.dropFailedTitle',
                warnMessage: translate('filmPrint.dropFailedBody', { error: drop.error ?? 'unknown' }),
                logKey: 'agent-drop-failed',
              });
            } else {
              reportOutcome(true, undefined, drop.destPath);
              dropSucceeded = true;
              clearNeedsManual(garmentUnitId);
              clearRetryState(garmentUnitId);
              if (headerless) {
                warn('filmPrint.headerSkippedTitle', translate('filmPrint.headerSkippedBody'));
              }
            }
          } else {
            // Neither composited bytes nor a presigned URL (e.g. Azure legacy
            // backend) — nothing to auto-drop; keep the manual path.
            nonDelivery({
              auditReason: 'no drop payload (no composited bytes or presigned URL)',
              warnTitleKey: 'filmPrint.dropUnavailableTitle',
              warnMessage: translate('filmPrint.dropUnavailableBody'),
              logKey: 'no-drop-payload',
            });
          }
          bucket = dropSucceeded ? 'dropped' : 'failed';
        } else {
          // No DTF hot folder configured at all — the film row is created
          // server-side but ZERO bytes can reach the RIP this run. This used
          // to be entirely silent (batch 1879's "10 sent, 8 landed, no
          // error" shape) — never again: always a warning + a `failed`
          // outcome, even though the unit itself doesn't throw. Uses
          // `fieryNotConfigured*` (names the ACTUAL root cause — the agent's
          // DTF drop folder isn't picked) rather than `dropUnavailable*`,
          // which points at a storage-backend problem this isn't.
          nonDelivery({
            auditReason: 'no DTF hot folder configured',
            warnTitleKey: 'filmPrint.fieryNotConfigured',
            warnMessage: translate('filmPrint.fieryNotConfiguredDesc'),
            logKey: 'no-hot-folder',
          });
          bucket = 'failed';
        }
        // Item 6 (6-lens review): a failed/skipped drop still lands here in
        // 'printing' — the SAME "At RIP" pill a real success shows. The
        // needsManual chip (rendered on the card below) is the only thing
        // that keeps a failed drop from looking identical to a good one once
        // this run's toast summary closes.
        setUnitStates((s) => ({ ...s, [garmentUnitId]: 'printing' }));
        // 'settle' for BOTH the good drop and the agent-side failures: those
        // leave a real film row plus the manual path, and re-running
        // `prepareFilm` cannot pick a hot folder or fix the agent — retrying
        // would only burn 12 seconds per unit and say the same thing 3×.
        return { attemptClass: 'settle', bucket, lines: unitLines };
      } catch (error) {
        // Leave the unit in `error` so the per-unit retry button is offered.
        setUnitStates((s) => ({ ...s, [garmentUnitId]: 'error' }));
        throw error;
      }
    },
    [markNeedsManual, clearNeedsManual, clearRetryState],
  );

  // ESCALATION (phase 50.1): the retry ladder has given up on a unit. File an
  // artwork issue on the operator's behalf — automation tells someone at exactly
  // the point it stops being able to help — and record what happened so the card
  // can say it out loud. Never throws: a failed report becomes a visible
  // `reportFailed` state ("tell your supervisor"), never a silent swallow.
  const escalateArtworkFailure = useCallback(async (
    garmentUnitId: string,
    attempts: number,
    supplyBlocked = false,
  ): Promise<ArtworkEscalation> => {
    const locationId = selectedLocationRef.current;
    let record: ArtworkEscalation = {
      attempts,
      issueFiled: false,
      healTriggered: false,
      reportFailed: true,
      noArtworkAttached: false,
      supplyBlocked,
    };
    if (locationId) {
      try {
        const res = await reportArtworkIssueRef.current.mutateAsync({ garmentUnitId, locationId });
        record = {
          attempts,
          issueFiled: res.issueRaised,
          healTriggered: res.healTriggered,
          // The backend answered but raised nothing (e.g. an open issue already
          // covers this garment) — that is still "someone has been told".
          reportFailed: false,
          // BLU-817: the report is where the station learns WHY. The escalation path
          // is terminal (own copy, no rethrow), so this flag is the only thing that
          // can turn the panel's "tap Retry" into "retrying will not help".
          noArtworkAttached: res.noArtworkAttached,
          supplyBlocked,
        };
      } catch (error) {
        console.warn('[film-print] reportArtworkIssue failed:', getUserMessage(error));
      }
    }
    setEscalations((s) => ({ ...s, [garmentUnitId]: record }));
    return record;
  }, []);

  /**
   * RETRY LADDER (Brett's spec, phase 50.1): run `prepareAndDropUnitCore` for
   * one unit, auto-retrying a RETRYABLE non-delivery up to
   * MAX_PREPARE_ATTEMPTS times ~PREPARE_RETRY_DELAY_MS apart, then escalating.
   *
   * Why retry at all: the backend fires its confirmed-absence mirror-heal on
   * every artwork miss, so the attempt AFTER a miss frequently succeeds because
   * the heal just landed. Why not retry everything: an unpicked hot folder or a
   * dead agent says the same thing three times, 8 seconds slower.
   *
   * Progress lives on the unit's own card ("Retrying (2/3)…" on the busy
   * button) — the page is never blocked and the operator can keep working other
   * cards. A single-unit run gets exactly ONE blocking modal, at the end.
   * A bulk run gets NONE: its per-unit result folds into the existing single
   * reconciliation summary (`bulkOutcome` below is recorded once per unit,
   * whatever the ladder did, so the summary's card-count invariant holds).
   */
  const prepareUnitWithLadder = useCallback(async (
    garmentUnitId: string,
    orderNumber: string,
    run?: { outcomes: BulkOutcomeCollector },
  ): Promise<void> => {
    const translate = tRef.current;
    const isBulk = !!run;
    // A fresh operator action (a tap, or this unit's turn in a bulk run) starts
    // a FRESH ladder — that is what keeps the Retry button honest after an
    // escalation. The counter is per-unit and in-memory only; nothing about a
    // burned ladder survives a shift.
    prepareAttemptsRef.current[garmentUnitId] = 0;
    for (let attempt = 1; attempt <= MAX_PREPARE_ATTEMPTS; attempt += 1) {
      prepareAttemptsRef.current[garmentUnitId] = attempt;
      let attemptClass: PrepareAttemptClass;
      let bucket: BulkUnitOutcome = 'failed';
      let lines: string[] = [];
      let supplyBlocked = false;
      try {
        const result = await prepareAndDropUnitCore(garmentUnitId, orderNumber, {
          raiseWarningModals: !isBulk,
        });
        attemptClass = result.attemptClass;
        bucket = result.bucket;
        lines = result.lines;
      } catch (error) {
        const artwork = parseFilmArtworkTransient(error);
        attemptClass = classifyPrepareAttempt({
          artworkTransient: artwork.isTransient,
          artworkPermanent: artwork.isPermanent,
        });
        // Phase 92: a broken shared-blank link is permanent. The backend has
        // already filed the issue; the card says "set it aside" in the
        // operator's language, not the server's English.
        supplyBlocked = artwork.isPermanent && artwork.detail === 'supply_mapping_invalid';
        if (attemptClass === 'settle') {
          // Not an artwork-delivery incident we model (network drop, auth, an
          // unmodelled guard): unchanged behaviour — no retry, no issue filed.
          // The single-unit caller shows the modal; a bulk run records it in
          // `runBulkTolerant`'s own failed list.
          clearRetryLabel(garmentUnitId);
          throw error;
        }
        // The core THREW before it could reach any of its own non-delivery
        // exits, so the shared triple hasn't run for this attempt. Run it here:
        // a backend-classified artwork failure must be audited, warned and
        // flagged exactly like a returned one — otherwise a unit that fails
        // three times with FILM_ARTWORK_TRANSIENT would carry no
        // FILM_DROP_FAILED prod_event and no needs-manual flag at all.
        const label = formatUnitLabel(garmentsByIdRef.current.get(garmentUnitId));
        const attemptLines: string[] = [];
        recordNonDelivery(
          garmentUnitId,
          {
            auditReason: `prepare failed: ${getUserMessage(error)}`,
            filename: null,
            warnTitleKey: 'filmPrint.prepFailedTitle',
            warnMessage: supplyBlocked ? translate('filmPrint.supplyBlockedDetail') : getUserMessage(error),
            severity: 'error',
            logKey: artwork.isPermanent ? 'artwork-permanent' : 'artwork-transient',
          },
          {
            reportOutcome: (success, reason, destPath, filename) => {
              const locationId = selectedLocationRef.current;
              if (!locationId) return;
              reportDropOutcomeRef.current
                .mutateAsync({
                  garmentUnitId,
                  locationId,
                  success,
                  filename: filename ?? null,
                  destPath: destPath ?? null,
                  error: reason ? reason.slice(0, 500) : null,
                })
                .catch(() => undefined);
            },
            markNeedsManual,
            // Collect the line only — the ladder raises the ONE terminal modal
            // below; a modal per attempt would interrupt one film three times.
            warn: (_titleKey, msg) => { attemptLines.push(`${label}: ${msg}`); },
            log: (line) => console.warn(line),
          },
        );
        lines = attemptLines;
      }

      const decision = decideNextAttempt({ attemptClass, attempt });

      if (decision === 'retry') {
        // Hold the card VISIBLY BUSY across the wait: the failed attempt left
        // it in 'printing'/'error', which would flash a tappable button for 4s
        // mid-ladder. 'preparing' + the attempt number keeps one honest state
        // ("Retrying (2/3)…") from the first tap to the last attempt.
        setUnitStates((s) => ({ ...s, [garmentUnitId]: 'preparing' }));
        setRetryAttempts((s) => ({ ...s, [garmentUnitId]: attempt + 1 }));
        await new Promise((resolve) => setTimeout(resolve, PREPARE_RETRY_DELAY_MS));
        continue;
      }

      // Terminal for this unit either way — drop the "Retrying" label first so
      // the card never sits on a stale attempt number.
      clearRetryLabel(garmentUnitId);

      if (decision === 'settle') {
        // NOTE: a unit that eventually succeeded contributes NO extra summary
        // line. "Sent after 2 tries" would flip an otherwise-clean bulk run to
        // "some films need attention", which would be a lie: everything on
        // screen reached the RIP.
        run?.outcomes.record(bucket, lines.length ? lines.join(' | ') : undefined);
        return;
      }

      // ESCALATE — nothing reached the RIP and the ladder is done.
      // 'error', never 'printing': after three attempts that delivered nothing,
      // the "At RIP" pill would be a lie, the unit would still be swept by the
      // batch-level "Mark all done" (state 'printing' is its eligibility), and
      // the card's own control would read "Resend to RIP" instead of the Retry
      // the escalation copy names. This is the state the blocker asked for —
      // no unit ever silently vanishes into "At RIP".
      setUnitStates((s) => ({ ...s, [garmentUnitId]: 'error' }));
      const escalation = await escalateArtworkFailure(garmentUnitId, attempt, supplyBlocked);
      // BLU-817: a line born without artwork gets its OWN title AND line. The generic
      // copy tells the operator to tap Retry, which cannot ever work here — that is
      // the exact complaint on the ticket. `reportFailed` still wins: if we could not
      // even tell anyone, that is the more urgent thing to say. Phase 92: a broken
      // shared-blank link gets "set it aside" copy. The selection lives in
      // `escalationCopyKeys`, shared with the card panel below.
      const copy = escalationCopyKeys(escalation);
      const escalationTitle = translate(copy.titleKey);
      const escalationLine = translate(copy.lineKey, { attempts: attempt, order: orderNumber });
      // Bulk runs show no modal, so this line is the ONLY place a bulk operator
      // learns the cause — it lands in the reconciliation summary.
      run?.outcomes.record('failed', [...lines, escalationLine].join(' | '));
      if (!isBulk) {
        // The ONE blocking modal for this unit's whole ladder (§6.1): what
        // happened, what was done about it, and which control to use next.
        feedbackRef.current.triggerError(
          escalationTitle,
          escalationLine,
          lines.length ? lines.join('\n') : undefined,
        );
      }
      return;
    }
  }, [prepareAndDropUnitCore, escalateArtworkFailure, clearRetryLabel, markNeedsManual]);

  // Per-unit "Prepare & Drop" (EXCEPTION path — a single failed/defective unit).
  const handlePrepareAndDrop = useCallback(
    async (garmentUnitId: string, orderNumber: string) => {
      try {
        await prepareUnitWithLadder(garmentUnitId, orderNumber);
      } catch (error) {
        feedbackRef.current.triggerError(tRef.current('filmPrint.prepFailedTitle'), getUserMessage(error));
      }
    },
    [prepareUnitWithLadder],
  );

  // "Download film" on a needs-manual card. One prepare per click (same cost and
  // safety as Resend: prepareFilm reuses the PRINTING film row and skips the
  // shelf claim), bytes saved as <id>__<name>, a storage URL opened in a new
  // tab. It does NOT change the unit's state or its needs-manual flag: the film
  // still needs a manual print and Mark Film Done.
  const handleDownloadFilm = useCallback(
    async (garmentUnitId: string, orderNumber: string) => {
      const locationId = selectedLocationRef.current;
      if (!locationId) return;
      await downloadFilmFile(garmentUnitId, () =>
        prepareMutationRef.current.mutateAsync({
          garmentUnitId,
          locationId,
          orderNumber,
          printTechnology: 'DTF',
          fulfillmentType: 'POD',
        }),
      );
    },
    [downloadFilmFile],
  );

  // Stable CORE: mark ONE unit's film done. THROWS on failure; does NOT beep
  // (the caller chimes — once per unit OR once per bulk run, not per unit in a
  // bulk loop, to avoid beep spam).
  const markDoneUnitCore = useCallback(async (garmentUnitId: string): Promise<void> => {
    const locationId = selectedLocationRef.current;
    if (!locationId) throw new Error('No location selected');
    const result = await markMutationRef.current.mutateAsync({ garmentUnitId, locationId });
    if (result.success) {
      setUnitStates((s) => ({ ...s, [garmentUnitId]: 'filmPrinted' }));
      // The operator manually resolved the "not sent" gap — clear the chip and
      // any escalation panel with it.
      clearNeedsManual(garmentUnitId);
      clearRetryState(garmentUnitId);
    }
  }, [clearNeedsManual, clearRetryState]);

  // Per-unit "Mark Film Done" (EXCEPTION path). Chimes once on success.
  const handleMarkDone = useCallback(async (garmentUnitId: string) => {
    try {
      await markDoneUnitCore(garmentUnitId);
      playSuccessBeep();
      void utils.filmPrint.getDtfGarmentsForBatch.invalidate();
    } catch (error) {
      feedbackRef.current.triggerError(tRef.current('filmPrint.markDoneFailedTitle'), getUserMessage(error));
    }
  }, [markDoneUnitCore, utils]);

  // Redo a DTF film via filmPrint.redoFilm (the shared catcher path, film-only):
  // it reverts the garment to PICKED, defects its film, releases the film's storage
  // slot, and records the redo reason (the PET film is the consumable — the
  // garment is untouched; distinct from the catcher garment-redo). On success the
  // card resets to `idle` so "Send to RIP" reappears for an immediate re-send.
  // Loud confirmation per §5.
  const handleRedoConfirm = useCallback(async (reason: string) => {
    const garmentUnitId = redoDialogGarmentId;
    const locationId = selectedLocationRef.current;
    if (!garmentUnitId || !locationId) return;
    try {
      const result = await redoFilmMutation.mutateAsync({
        garmentUnitId,
        locationId,
        reason,
      });
      if (result.success) {
        setUnitStates((s) => ({ ...s, [garmentUnitId]: 'idle' }));
        // Clear any stale pre-printed shelf-claim cue for this garment.
        setShelfClaims((s) => {
          if (!(garmentUnitId in s)) return s;
          const next = { ...s };
          delete next[garmentUnitId];
          return next;
        });
        // A redo starts a fresh send cycle — any stale "not sent" cue,
        // escalation panel, or burned retry ladder from a prior attempt no
        // longer applies.
        clearNeedsManual(garmentUnitId);
        clearRetryState(garmentUnitId);
        setRedoDialogGarmentId(null);
        playSuccessBeep();
        void utils.filmPrint.getDtfGarmentsForBatch.invalidate();
      }
    } catch (error) {
      setRedoDialogGarmentId(null);
      feedbackRef.current.triggerError(tRef.current('filmPrint.redoFailedTitle'), getUserMessage(error));
    }
  }, [redoDialogGarmentId, redoFilmMutation, utils, clearNeedsManual, clearRetryState]);

  // Format a `runBulkTolerant` prepare-failure into the same `#<order> <sku>:
  // <reason>` shape prepareAndDropUnitCore's own warnings use, so the UNIONED
  // summary line list reads consistently regardless of WHERE in the run a
  // unit fell over.
  const formatPrepareFailureLine = useCallback((id: string, error: string): string => {
    const g = garmentsByIdRef.current.get(id);
    return `${formatUnitLabel(g)}: ${error}`;
  }, []);

  // Surface ONE reconciling summary for a bulk prepare/resend run — replaces
  // the old mutually-exclusive "prep failures OR drop warnings" branch (batch
  // 1879: one prepare failure discarded every drop-failure line). Counts
  // ALWAYS reconcile against the cards on screen (`skipped` states any unit
  // this run intentionally excluded, e.g. already printing/done).
  const reportBulkDropSummary = useCallback((args: {
    eligible: number;
    outcomes: BulkOutcomeCollector;
    prepareFailed: { id: string; error: string }[];
    playChimeOnClean?: boolean;
  }) => {
    const { eligible, outcomes, prepareFailed, playChimeOnClean } = args;
    const translate = tRef.current;
    const counts = outcomes.counts();
    const dropWarnings = [
      ...prepareFailed.map((f) => formatPrepareFailureLine(f.id, f.error)),
      ...outcomes.lines(),
    ];
    const cardsOnScreen = filteredGarmentsRef.current.length;
    const summary = buildBulkDropSummary({
      cardsOnScreen,
      eligible,
      dropped: counts.dropped,
      shelfClaimed: counts.shelfClaimed,
      // Prepare failures never reached the outcomes collector (the core
      // throws before recording) — they still count as `failed`.
      failed: counts.failed + prepareFailed.length,
      dropWarnings,
      agentHasDtf: agentRef.current.hasDtf,
    });
    if (summary.severity === 'success') {
      if (playChimeOnClean) playSuccessBeep(); // ONE chime — the whole batch reconciled clean.
      // BLU-818 item 1 — PRODUCTION_DESIGN §10: "Long-running prints without explicit
      // confirmation. Operator may walk away. Always end a print job with an 'OK, all
      // printed' confirmation." A clean run used to end on that chime alone, and a
      // 4-minute bulk drop is exactly when the operator is not at the screen to hear
      // it: they came back to a page indistinguishable from one where nothing had
      // happened, because a successful drop and a failed one both leave the "At RIP"
      // pill. This is the acknowledgement that was missing — and it is the real fix
      // for "assumed failure and walked away", not the per-card timer.
      // Success severity, NOT warning: triggerWarning plays the error beep and renders
      // amber, so on the floor — where that beep means "something is wrong" — the
      // acknowledgement added to stop operators assuming failure would have signalled
      // exactly that. The chime above is the audio twin (§5.2).
      // `dropped` ONLY. A shelf-claimed film was pulled from pre-printed stock and
      // never reached the RIP, so folding it into "sent to the RIP" would tell the
      // operator to go collect films from a printer that never printed them.
      feedbackRef.current.triggerSuccessModal(
        counts.shelfClaimed > 0
          ? translate('filmPrint.bulkCompleteWithShelfTitle', {
            count: counts.dropped, shelf: counts.shelfClaimed,
          })
          : translate('filmPrint.bulkCompleteTitle', { count: counts.dropped }),
        translate('filmPrint.bulkCompleteBody'),
      );
      return;
    }
    // Summary + the per-film lines (order + SKU + reason) — the operator
    // needs to know WHICH films to print manually, not just how many
    // (batch 1672/1879: a silent miss meant diffing the RIP folder by hand).
    // The list rides in secondaryMessage (whitespace-pre-line block).
    // Respect severity: an `error` run (something needs attention) is louder
    // than a `warning` run (e.g. skipped-only) — triggerError vs triggerWarning.
    const trigger = summary.severity === 'error' ? feedbackRef.current.triggerError : feedbackRef.current.triggerWarning;
    trigger(
      translate(summary.titleKey, { ...summary.bodyParams }),
      translate(summary.bodyKey, { ...summary.bodyParams }),
      summary.lines.length ? summary.lines.join('\n') : undefined,
    );
  }, [formatPrepareFailureLine]);

  // BULK "Prepare & Drop All" / "Resend all to RIP" — SAME loop shape
  // (prepareAndDropUnitCore over an eligible set, ONE reconciling summary at
  // the end), differing only in eligibility + kind + whether a clean run
  // chimes. TOLERANT — one failing unit doesn't abort the rest (each failure
  // is left in `error` for individual retry). Progress shown ("Preparing
  // 3/10"). An in-flight guard stops a double-tap re-entering.
  const runBulkDrop = useCallback(async (args: {
    kind: 'prepare' | 'resend';
    isEligible: (g: DtfGarmentForFilm) => boolean;
    playChimeOnClean?: boolean;
  }) => {
    const { kind, isEligible, playChimeOnClean } = args;
    if (bulkRunningRef.current) return;
    const eligible = filteredGarmentsRef.current.filter(isEligible);
    if (eligible.length === 0) return;
    bulkRunningRef.current = true;
    const outcomes = makeOutcomeCollector();
    setBulkRun({ kind, done: 0, total: eligible.length });
    try {
      const result = await runBulkTolerant(
        eligible.map((g) => g.garmentUnitId),
        async (id) => {
          const g = garmentsByIdRef.current.get(id);
          // The ladder records EXACTLY ONE outcome per unit into `outcomes`,
          // whatever its retries did — so the summary still reconciles against
          // the cards on screen.
          await prepareUnitWithLadder(id, g?.orderNumber ?? '', { outcomes });
        },
        { onProgress: (done, total) => setBulkRun({ kind, done, total }) },
      );
      reportBulkDropSummary({ eligible: eligible.length, outcomes, prepareFailed: result.failed, playChimeOnClean });
    } finally {
      setBulkRun(null);
      bulkRunningRef.current = false;
    }
  }, [prepareUnitWithLadder, reportBulkDropSummary]);

  const handleBulkPrepareAndDrop = useCallback(() => runBulkDrop({
    kind: 'prepare',
    isEligible: (g) => {
      const st = unitStatesRef.current[g.garmentUnitId] ?? 'idle';
      return st === 'idle' || st === 'error';
    },
    // A clean bulk prepare should chime like a clean resend — silence here
    // was an inconsistency, not a deliberate choice.
    playChimeOnClean: true,
  }), [runBulkDrop]);

  // Resend: prepareFilm is idempotent for a PRINTING film (the DAO reuses the
  // row), so this re-resolves + re-drops every file without creating
  // duplicate film units — the recovery path when drops land wrong at the
  // RIP (stale hot-folder files, agent offline mid-batch, presign 404s —
  // batch 1717): one tap re-sends the whole batch instead of tapping Resend
  // on every card.
  const handleBulkResend = useCallback(() => runBulkDrop({
    kind: 'resend',
    isEligible: (g) => (unitStatesRef.current[g.garmentUnitId] ?? 'idle') === 'printing',
    playChimeOnClean: true,
  }), [runBulkDrop]);

  // BULK "Mark all done" (batch-first primary action). Loops the stable mark
  // core over every unit in `printing` (eligible) state. TOLERANT + progress;
  // ONE success chime at the END (not per unit). In-flight guarded.
  const handleBulkMarkDone = useCallback(async () => {
    if (bulkRunningRef.current) return;
    const eligible = filteredGarmentsRef.current.filter(
      (g) => (unitStatesRef.current[g.garmentUnitId] ?? 'idle') === 'printing',
    );
    if (eligible.length === 0) return;
    bulkRunningRef.current = true;
    const translate = tRef.current;
    setBulkRun({ kind: 'markDone', done: 0, total: eligible.length });
    try {
      const result = await runBulkTolerant(
        eligible.map((g) => g.garmentUnitId),
        async (id) => { await markDoneUnitCore(id); },
        { onProgress: (done, total) => setBulkRun({ kind: 'markDone', done, total }) },
      );
      if (result.succeeded.length > 0) {
        playSuccessBeep(); // ONE chime for the whole batch (avoid beep spam).
        void utils.filmPrint.getDtfGarmentsForBatch.invalidate();
      }
      if (result.failed.length > 0) {
        feedbackRef.current.triggerWarning(
          translate('filmPrint.bulkMarkDoneFailuresTitle'),
          translate('filmPrint.bulkMarkDoneFailuresBody', {
            failed: result.failed.length,
            total: eligible.length,
          }),
        );
      }
    } finally {
      setBulkRun(null);
      bulkRunningRef.current = false;
    }
  }, [markDoneUnitCore, utils]);

  // BULK "Redo all films" (batch-scoped exception action). Scraps + re-sends
  // every film still AT this station with a film sent (see isBulkRedoable —
  // LABEL_PRINTED/PICKED "At RIP" + FILM_PRINTED; PRESSED excluded so a batch-wide
  // tap can't scrap already-pressed work) in one tap, via the same
  // filmPrint.redoFilm path as the per-unit redo:
  // reverting each garment to PICKED, defecting its film, releasing its storage
  // slot. ONE shared reason (captured by the confirm dialog). TOLERANT
  // (one failing unit doesn't abort the rest) + progress; ONE summary warning;
  // ONE success chime at the end. In-flight guarded.
  const handleBulkRedoFilms = useCallback(async (reason: string) => {
    if (bulkRunningRef.current) return;
    const locationId = selectedLocationRef.current;
    if (!locationId) return;
    const eligible = filteredGarmentsRef.current.filter(isBulkRedoable);
    setBulkRedoOpen(false);
    if (eligible.length === 0) return;
    bulkRunningRef.current = true;
    const translate = tRef.current;
    setBulkRun({ kind: 'redo', done: 0, total: eligible.length });
    try {
      const result = await runBulkTolerant(
        eligible.map((g) => g.garmentUnitId),
        async (id) => {
          const res = await redoFilmMutation.mutateAsync({
            garmentUnitId: id,
            locationId,
            reason,
          });
          if (!res.success) throw new Error('redo did not apply');
          // Reverted to PICKED — reset the card so "Send to RIP" reappears, and
          // clear any stale pre-printed shelf-claim cue, "not sent" cue and
          // burned retry ladder for this garment.
          setUnitStates((s) => ({ ...s, [id]: 'idle' }));
          setShelfClaims((s) => {
            if (!(id in s)) return s;
            const next = { ...s };
            delete next[id];
            return next;
          });
          clearNeedsManual(id);
          clearRetryState(id);
        },
        { onProgress: (done, total) => setBulkRun({ kind: 'redo', done, total }) },
      );
      if (result.succeeded.length > 0) {
        playSuccessBeep(); // ONE chime for the whole batch (avoid beep spam).
        void utils.filmPrint.getDtfGarmentsForBatch.invalidate();
      }
      if (result.failed.length > 0) {
        feedbackRef.current.triggerWarning(
          translate('filmPrint.bulkRedoFailuresTitle'),
          translate('filmPrint.bulkRedoFailuresBody', {
            failed: result.failed.length,
            total: eligible.length,
          }),
        );
      }
    } finally {
      setBulkRun(null);
      bulkRunningRef.current = false;
    }
  }, [redoFilmMutation, utils, clearNeedsManual, clearRetryState]);

  // Cross-batch order search (NO batch open): a SERVER query
  // (findDtfBatchesForOrder), never a client-side filter over the paginated
  // `listDtfBatches` page (CLAUDE.md §10 filter-layer rule). One match opens it
  // directly; several present a short pick list; none surfaces a clear
  // not-found message.
  const handleCrossBatchSearch = useCallback(async () => {
    const locationId = selectedLocationRef.current;
    const query = orderSearch.trim();
    if (!locationId || !query) return;
    // Stale-response guard (item 8, 6-lens review): a monotonically
    // increasing request token so a slower EARLIER search can never clobber
    // a faster LATER one's results, and a location switch mid-flight can
    // never apply a result scoped to the OLD location.
    const myToken = ++crossBatchSearchTokenRef.current;
    setCrossBatchSearching(true);
    setCrossBatchMatches(null);
    try {
      const { batches } = await utilsRef.current.filmPrint.findDtfBatchesForOrder.fetch({
        orderNumber: query,
        locationId,
      });
      if (myToken !== crossBatchSearchTokenRef.current || selectedLocationRef.current !== locationId) {
        return; // superseded by a newer search, or the location changed mid-flight
      }
      if (batches.length === 1 && batches[0]) {
        // BLU-818 item 3: carry the searched order INTO the batch instead of dropping
        // it, so the operator lands on just that order's films. Hand off BEFORE the
        // batch change so the ref is set when the `[batchId]` effect consumes it.
        handOffOrderSearch(query);
        setBatchId(batches[0].batchId);
      } else if (batches.length === 0) {
        feedbackRef.current.triggerWarning(
          tRef.current('filmPrint.orderSearchNoMatchesTitle'),
          tRef.current('filmPrint.orderSearchNoMatches', { query }),
        );
      } else {
        setCrossBatchMatches(batches);
      }
    } catch (error) {
      if (myToken !== crossBatchSearchTokenRef.current || selectedLocationRef.current !== locationId) {
        return;
      }
      feedbackRef.current.triggerError(tRef.current('filmPrint.scanGarmentBatchError'), getUserMessage(error));
    } finally {
      if (myToken === crossBatchSearchTokenRef.current) {
        setCrossBatchSearching(false);
      }
    }
  }, [orderSearch]);

  // Stable CORE: resolve a scanned garment against the DB-authoritative
  // getBatchForGarment lookup (the same query the scanner-first flow already
  // uses). Returns the resolved batchId, or `null` when the DB ITSELF says
  // the unit is not DTF (getBatchForGarment's own guard reading
  // print_technology) — the only signal allowed to open the cross-tech
  // mismatch dialog. Rethrows any other failure (not-found, network) so callers keep
  // their own error copy. A scanned label's `m` field is never consulted
  // here — it's baked in at label-print time and goes stale once the unit's
  // DB print_technology is corrected afterward (2026-08-08 incident: 6 Mini
  // Zali CBP-CHOC units re-stamped DTG→DTF).
  const resolveDtfBatchForGarment = useCallback(async (garmentId: string): Promise<string | null> => {
    const locationId = selectedLocationRef.current;
    if (!locationId) throw new Error('No location selected');
    try {
      const { batchId: resolvedBatch } = await utilsRef.current.filmPrint.getBatchForGarment.fetch({
        garmentUnitId: garmentId,
        locationId,
      });
      return resolvedBatch;
    } catch (error) {
      if (parseNotDtfUnitBlocked(error)) return null;
      throw error;
    }
  }, []);

  // Context-aware scan (Fix 7a):
  //  - No batch selected → scan a batch QR (THP://BATCH:<uuid> or bare uuid).
  //  - Batch selected     → scan a GARMENT QR to advance that unit (relay to
  //    markFilmPrinted) so scan-to-advance keeps working in the detail view.
  const handleScan = useCallback((qrCode: string) => {
    hasScannedRef.current = true;
    if (!batchId) {
      // Scanner-first entry: the app never prints a batch QR, so the real
      // scanner path is "scan a GARMENT QR to open its batch". We resolve the
      // garment's batch via the DB-authoritative getBatchForGarment query,
      // then open it — its own DTF guard (not the scanned label) decides a
      // cross-tech bounce below. (A bare/batch UUID is still accepted as a
      // fallback for any QR that decodes to a UUID but isn't a garment
      // payload.)
      const garmentId = extractGarmentId(qrCode);
      const locationId = selectedLocationRef.current;
      if (garmentId && locationId) {
        if (resolvingGarmentScanRef.current) return; // in-flight guard
        resolvingGarmentScanRef.current = true;
        void (async () => {
          try {
            const resolvedBatch = await resolveDtfBatchForGarment(garmentId);
            if (resolvedBatch === null) {
              // BLU-233 / BLU-1509: DB-confirmed DTG. Ask: go to the DTG
              // printer (carrying ?garment=<id>) or do it DTF here.
              openMismatch(garmentId);
              return;
            }
            setFocusGarmentId(garmentId); // highlight the scanned unit once open
            setBatchId(resolvedBatch);
          } catch (error) {
            feedbackRef.current.triggerError(
              tRef.current('filmPrint.scanGarmentBatchError'),
              getUserMessage(error),
            );
          } finally {
            resolvingGarmentScanRef.current = false;
          }
        })();
        return;
      }
      // Fallback: a bare/batch UUID (no garment payload).
      const uuidRe = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
      const m = qrCode.match(uuidRe);
      if (m) {
        setBatchId(m[0]);
      } else {
        feedback.triggerError(t('filmPrint.scanGarmentToOpenError'), t('filmPrint.scanGarmentToOpenErrorDesc'));
      }
      return;
    }
    // Detail view: scan a garment QR to advance it.
    const garmentId = extractGarmentId(qrCode);
    if (!garmentId) {
      feedback.triggerError(t('filmPrint.scanGarmentError'), t('filmPrint.scanGarmentErrorDesc'));
      return;
    }
    if (knownIdsRef.current.has(garmentId)) {
      // Already DB-confirmed DTF via THIS loaded batch (getDtfGarmentsForBatch
      // only returns print_technology='DTF' rows) — never re-litigate against
      // a possibly-stale label; advance it.
      void relayDone(garmentId);
      return;
    }
    // Not in the currently loaded batch. ALWAYS resolve via the DB
    // (getBatchForGarment's own print_technology guard) before deciding
    // between "wrong batch" and "wrong station" — a same-tech-LOOKING label
    // is no more trustworthy than a cross-tech-looking one once the unit's
    // DB print_technology has been corrected after the label was printed
    // (2026-08-08 incident); the scanned QR's hint must never be the final
    // word either way.
    if (resolvingGarmentScanRef.current) return; // in-flight guard
    resolvingGarmentScanRef.current = true;
    void (async () => {
      try {
        const resolvedBatch = await resolveDtfBatchForGarment(garmentId);
        if (resolvedBatch === null) {
          openMismatch(garmentId);
          return;
        }
        // DB confirms DTF — just not part of the currently open batch.
      } catch {
        // Fall through to the standard "not in this batch" message below.
      } finally {
        resolvingGarmentScanRef.current = false;
      }
      feedbackRef.current.triggerError(
        tRef.current('filmPrint.scanNotInBatch'),
        tRef.current('filmPrint.scanNotInBatchDesc'),
      );
    })();
  }, [batchId, feedback, t, relayDone, resolveDtfBatchForGarment, openMismatch]);

  // BLU-1509 "Do this DTF instead": the safe in-place flip, then open the
  // shirt's batch with its card highlighted, as a scan of a DTF shirt does.
  const mismatchGarmentId = mismatch.garmentId;
  const handleSwitchToDtf = useCallback(async (): Promise<PrintTechnologySwitchAttempt> => {
    const locationId = selectedLocationRef.current;
    if (!mismatchGarmentId || !locationId) return { ok: false, errorCode: null };
    const result = await switchToDtf({ garmentUnitId: mismatchGarmentId, locationId });
    if (!result.ok) return { ok: false, errorCode: result.errorCode };
    // A retry that found the shirt already DTF has no batch: look it up like a scan.
    const openBatchId =
      result.batchId ?? (await resolveDtfBatchForGarment(mismatchGarmentId).catch(() => null));
    closeSwitched();
    void utils.filmPrint.listDtfBatches.invalidate();
    if (openBatchId) {
      void utils.filmPrint.getDtfGarmentsForBatch.invalidate({ batchId: openBatchId });
      setFocusGarmentId(mismatchGarmentId);
      setBatchId(openBatchId);
    }
    return { ok: true };
  }, [mismatchGarmentId, switchToDtf, resolveDtfBatchForGarment, closeSwitched, utils]);

  // Show error modal on garments-load failure (feedback excluded from deps —
  // see CatcherPage gotcha: feedback object is recreated every render).
  useEffect(() => {
    if (!batchId) return;
    const err = garmentsQuery.error?.message ?? null;
    if (err && shownErrorRef.current !== err) {
      shownErrorRef.current = err;
      feedback.triggerError(t('filmPrint.loadError'), getUserMessage(garmentsQuery.error));
    } else if (!err) {
      shownErrorRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `feedback` is intentionally not memoized; see memory feedback_use_scan_feedback_unstable
  }, [garmentsQuery.error, batchId, t]);

  // Memoize so the `?? []` fallback doesn't create a new array identity each
  // render (which would re-run doneCount's useMemo every render).
  const garments = useMemo(
    () => garmentsQuery.data?.garments ?? [],
    [garmentsQuery.data],
  );
  // BLU-817: garments the server withheld because their order line was born without
  // artwork. NEVER folded into `garments` — Brett's decision is no dead cards, and
  // every reconciliation counter derives from the returned list, so keeping these out
  // is exactly what keeps `dropped + shelfClaimed + failed + skipped === cardsOnScreen`
  // true. Surfaced as one advisory line instead, because the bulk buttons' counts are
  // now short by these films and the operator has to know why.
  const hiddenForArtworkCount = garmentsQuery.data?.hiddenForArtworkIssues?.count ?? 0;
  const doneCount = useMemo(
    () => garments.filter((g) => unitStates[g.garmentUnitId] === 'filmPrinted').length,
    [garments, unitStates],
  );

  // In-batch order-number filter — narrows the already-loaded, UNPAGINATED
  // `getDtfGarmentsForBatch` result. Tolerant of the '#' prefix and case.
  // filter-layer-ok: getDtfGarmentsForBatch returns the whole batch — nothing
  // paginates at any layer, so filtering the loaded array in the component is
  // the correct layer (CLAUDE.md §10).
  const normalizedOrderSearch = useMemo(
    () => stripOrderNamePrefix(orderSearch).toLowerCase(),
    [orderSearch],
  );
  const filteredGarments = useMemo(() => {
    if (!normalizedOrderSearch) return garments;
    return garments.filter((g) => stripOrderNamePrefix(g.orderNumber).toLowerCase().includes(normalizedOrderSearch));
  }, [garments, normalizedOrderSearch]);

  // Batch-wide lightbox: ONE FLAT LIST of every image in the batch (artwork +
  // product preview interleaved per unit) so the arrows flick across the whole
  // batch, from the SAME per-unit builder the cards render from. Only signed
  // URLs already on the page — nothing full-size loads until the dialog opens
  // (the lightbox loads the current image + pre-warms its two neighbours only).
  const lightboxData = useMemo(
    () =>
      buildProductionLightboxData(
        garments.map((g) => ({
          subjectId: g.garmentUnitId,
          // The subject names the row; the lightbox derives the rest of the
          // caption from whichever source is actually rendering.
          subject: `#${formatOrderNumber(g.orderNumber)} ${g.shopifySku ?? g.sku}`,
          entries: buildFilmUnitPreviewEntries(g, 'full'),
        })),
      ),
    [garments],
  );
  const lightbox = useProductionLightbox({
    entries: lightboxData.entries,
    indexByKey: lightboxData.indexByKey,
    resetKey: batchId,
  });
  const openLightboxAt = lightbox.openAt;
  const openLightbox = useCallback(
    (garmentUnitId: string, kind: ProductionPreviewKind, sourceIndex: number) => {
      openLightboxAt(productionPreviewKey(garmentUnitId, kind), sourceIndex);
    },
    [openLightboxAt],
  );

  // Pause the scanner ONLY while a blocking modal is open (Fix 7a). The detail
  // (batch-selected) view still needs scan-to-advance, so we must NOT pause on
  // `!!batchId` — the global refocus listener must keep routing keystrokes to
  // the hidden QRScannerInput mounted in the detail view (mirrors the canonical
  // PrintStationPage, which only pauses on modals/dialogs).
  useScannerAutoFocus({
    paused: feedback.isModalOpen || mismatch.open || redoDialogGarmentId !== null || bulkRedoOpen || lightbox.open,
  });

  // Keep the bulk-loop refs fresh (stable bulk callbacks read the latest list).
  // Rebuilt only when the list changes. First unit wins on a duplicate id, the
  // same unit the find() calls this replaced would have returned.
  useEffect(() => {
    const byId = new Map<string, DtfGarmentForFilm>();
    for (const g of garments) {
      if (!byId.has(g.garmentUnitId)) byId.set(g.garmentUnitId, g);
    }
    garmentsByIdRef.current = byId;
  }, [garments]);
  useEffect(() => { filteredGarmentsRef.current = filteredGarments; }, [filteredGarments]);

  // The open batch (for the station-scale header identity). Resolved from the
  // location's batch list; null when opened via a garment scan whose batch fell
  // outside the list (header then falls back to the station title).
  const selectedBatch = useMemo(
    () => (batchesQuery.data?.batches ?? []).find((b) => b.id === batchId) ?? null,
    [batchesQuery.data, batchId],
  );
  // What the "no DTF units here" state can HONESTLY say (MZ day-1: operators hit
  // a bare "No DTF garments in this batch." and concluded the app had hidden
  // their work — it hadn't; every item was routed DTG because its SKU carried no
  // print-technology tag). Derived from the batch-header counters the batch list
  // already carries (`prod_batches.total_pod_items` / `total_dtf_items`) — no
  // extra fetch, no new endpoint.
  //   'dtgCount'            — we know exactly how many printed items went DTG.
  //   'dtgLikely'           — the batch header isn't on hand (batch opened by
  //                           scan, so it isn't in the DTF-filtered list) or
  //                           predates the DTF counter: name the cause, skip
  //                           the number.
  //   'cancelledOrSuperseded' — the counters themselves rule out DTG: every
  //                           POD item in the batch IS counted DTF
  //                           (nonDtf === 0), yet nothing loaded — asserting
  //                           "these are DTG" here would be a lie the
  //                           counters directly contradict. The honest
  //                           explanation is that the DTF items were
  //                           cancelled/superseded after the counters were
  //                           written.
  //   'unknown'             — the batch has no print-on-demand items at all;
  //                           asserting "these are DTG" would be a lie, so we
  //                           say nothing extra.
  //   'hiddenNoArtwork'     — BLU-817: the batch's DTF garments exist but every one
  //                           was withheld because its order line was born without
  //                           artwork. Checked FIRST and deliberately so: the
  //                           counters would otherwise land on
  //                           'cancelledOrSuperseded' and assert the films were
  //                           cancelled, which is simply false — and the sr-only
  //                           region would announce that lie too.
  const emptyBatchReason = useMemo((): {
    kind: 'dtgCount' | 'dtgLikely' | 'cancelledOrSuperseded' | 'unknown' | 'hiddenNoArtwork';
    count: number;
    batchNumber: number | null;
  } => {
    const podItems = selectedBatch?.total_pod_items ?? null;
    const dtfItems = selectedBatch?.total_dtf_items ?? null;
    const batchNumber = selectedBatch?.batch_number ?? null;
    if (hiddenForArtworkCount > 0) {
      return { kind: 'hiddenNoArtwork', count: hiddenForArtworkCount, batchNumber };
    }
    if (podItems === 0) return { kind: 'unknown', count: 0, batchNumber };
    if (podItems !== null && dtfItems !== null && batchNumber !== null) {
      const nonDtf = podItems - dtfItems;
      if (nonDtf > 0) return { kind: 'dtgCount', count: nonDtf, batchNumber };
      if (podItems > 0 && nonDtf === 0) {
        return { kind: 'cancelledOrSuperseded', count: 0, batchNumber };
      }
    }
    return { kind: 'dtgLikely', count: 0, batchNumber };
  }, [selectedBatch, hiddenForArtworkCount]);

  // Copy for the empty state, resolved once so the sr-only live region below can
  // announce the same words the operator reads (one live region, no drift).
  const emptyBatchCopy = useMemo(() => {
    if (emptyBatchReason.kind === 'unknown') {
      return { title: t('filmPrint.noDtfGarments'), detail: null, showDtgLink: false };
    }
    if (emptyBatchReason.kind === 'hiddenNoArtwork') {
      return {
        title: t('filmPrint.allHiddenNoArtwork', { count: emptyBatchReason.count }),
        detail: null,
        // The DTG station is not the answer here — nothing can print anywhere.
        showDtgLink: false,
      };
    }
    if (emptyBatchReason.kind === 'cancelledOrSuperseded') {
      return { title: t('filmPrint.dtfItemsCancelledOrSuperseded'), detail: null, showDtgLink: false };
    }
    return {
      title:
        emptyBatchReason.kind === 'dtgCount'
          ? t('filmPrint.noDtfInBatch', { number: emptyBatchReason.batchNumber })
          : t('filmPrint.noDtfInThisBatch'),
      detail:
        emptyBatchReason.kind === 'dtgCount'
          ? t('filmPrint.dtgItemsInBatch', { count: emptyBatchReason.count })
          : t('filmPrint.dtgItemsHint'),
      showDtgLink: true,
    };
  }, [emptyBatchReason, t]);

  // Raw batch-status enum → localized friendly label (never show the DB token).
  const friendlyBatchStatus = useCallback(
    (status: string) => {
      const key = BATCH_STATUS_LABEL_KEYS[status];
      return key ? t(key) : status;
    },
    [t],
  );

  // Counts that drive the bulk buttons' enabled state + labels — derived from
  // filteredGarments so the count on the button ALWAYS matches what the bulk
  // action will actually sweep (an active search narrows both together).
  // tabular-nums in the UI for steady-width counters.
  const prepareableCount = useMemo(
    () => filteredGarments.filter((g) => {
      const st = unitStates[g.garmentUnitId] ?? 'idle';
      return st === 'idle' || st === 'error';
    }).length,
    [filteredGarments, unitStates],
  );
  const markableCount = useMemo(
    () => filteredGarments.filter((g) => (unitStates[g.garmentUnitId] ?? 'idle') === 'printing').length,
    [filteredGarments, unitStates],
  );
  // Redoable films for the batch-level "Redo all films" action — films still AT
  // this station with a film actually sent (see isBulkRedoable). Excludes PRESSED
  // (already pressed onto a garment): a batch-wide tap must not scrap pressed work;
  // the single-unit redo still covers the PRESSED edge.
  const redoableCount = useMemo(
    () => filteredGarments.filter(isBulkRedoable).length,
    [filteredGarments],
  );

  // BLU-233: when the cross-tech-handed-over garment becomes visible (its batch
  // is opened + list loaded), scroll its card into view and clear the highlight
  // after a beat so it reads as a transient "here it is" cue. Graceful no-op
  // while the garment isn't in the current batch.
  useEffect(() => {
    if (!focusGarmentId) return;
    if (!garments.some((g) => g.garmentUnitId === focusGarmentId)) return;
    const el = focusCardRef.current;
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const timer = setTimeout(() => setFocusGarmentId(null), 4000);
    return () => clearTimeout(timer);
  }, [focusGarmentId, garments]);

  // Location guard.
  if (!selectedLocation) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl animate-enter">
        <Card>
          <CardContent className="py-16 text-center">
            <Film className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">{t('filmPrint.selectLocation')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter flex flex-col min-h-[calc(100dvh-4.5rem)]">
      {/* ARIA live region (Fix 7c — i18n, not hardcoded English) */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {garmentsQuery.isLoading && t('lookingUp')}
        {bulkRun?.kind === 'prepare'
          && t('filmPrint.bulkPreparingProgress', { done: bulkRun.done, total: bulkRun.total })}
        {bulkRun?.kind === 'resend'
          && t('filmPrint.bulkResendProgress', { done: bulkRun.done, total: bulkRun.total })}
        {bulkRun?.kind === 'markDone'
          && t('filmPrint.bulkMarkingProgress', { done: bulkRun.done, total: bulkRun.total })}
        {bulkRun?.kind === 'redo'
          && t('filmPrint.bulkRedoProgress', { done: bulkRun.done, total: bulkRun.total })}
        {/* An empty batch announces the REASON, not "0 DTF garments" — the bare
            count is what read as "the app lost my work". Gated on !isError —
            a load FAILURE must announce the error state (the modal below),
            never this reassuring-sounding empty-batch copy. */}
        {!bulkRun && batchId && !garmentsQuery.isLoading && !garmentsQuery.isError && garments.length === 0
          && [emptyBatchCopy.title, emptyBatchCopy.detail].filter(Boolean).join('. ')}
        {!bulkRun && batchId && garments.length > 0
          && t('filmPrint.liveStatus', { total: garments.length, done: doneCount })}
      </div>

      {/* Header — station-scale batch identity when a batch is open (§4.2:
          order/garment number front and centre), compact station title on the
          no-batch landing. */}
      <div className="flex items-center gap-3">
        {batchId && (
          <Button variant="ghost" size="icon" onClick={() => setBatchId(null)} className="shrink-0 h-12 w-12" aria-label={t('backTo', { station: t('filmPrint.title') })}>
            <ArrowLeft className="h-6 w-6" />
          </Button>
        )}
        <div className="p-2 rounded-lg bg-indigo-500/10 shrink-0">
          <Film className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        {batchId ? (
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-mono font-semibold tabular-nums">
                {selectedBatch
                  ? t('filmPrint.batchLabel', { number: selectedBatch.batch_number })
                  : t('filmPrint.title')}
              </h1>
              {selectedBatch && (
                <Badge variant="secondary" className="text-sm">
                  {friendlyBatchStatus(selectedBatch.status)}
                </Badge>
              )}
            </div>
            {garments.length > 0 && (
              <p className="text-base font-medium text-muted-foreground tabular-nums">
                {t('filmPrint.progress', { done: doneCount, total: garments.length })}
              </p>
            )}
          </div>
        ) : (
          <div>
            <h1 className="text-2xl font-semibold">{t('filmPrint.title')}</h1>
            <p className="text-muted-foreground">{t('filmPrint.description')}</p>
          </div>
        )}
      </div>

      <ConnectionStatusBanner heartbeat={heartbeat} />

      {/* Agent / Fiery status banner. Healthy state shows WHERE films will
          drop — the operator should never have to guess which folder the
          agent validated (batch 1672: "dropped, but where?"). */}
      {!agent.isLoading && agent.isConnected && agent.hasDtf && agent.hotFolder && (
        <p className="text-sm text-muted-foreground truncate" title={agent.hotFolder}>
          {t('filmPrint.dropFolder')} <span className="font-mono">{agent.hotFolder}</span>
        </p>
      )}
      {!agent.isLoading && (!agent.isConnected || !agent.hasDtf) && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
          <WifiOff className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-base font-semibold text-amber-800 dark:text-amber-200">
              {!agent.isConnected ? t('filmPrint.agentNotDetected') : t('filmPrint.fieryNotConfigured')}
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              {!agent.isConnected ? t('filmPrint.agentNotDetectedDesc') : t('filmPrint.fieryNotConfiguredDesc')}
            </p>
          </div>
        </div>
      )}

      <div className="flex-1 space-y-6">
        {/* No batch selected. Batch-first UX: the "Open Batches" list is the
            PRIMARY entry (prominent, top). The scan box is repurposed to the
            real scanner-first path — "scan a garment to open its batch" (the app
            never prints a batch QR). */}
        {!batchId && (
          <>
            {/* PRIMARY — pick an open batch. */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Layers className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  {t('filmPrint.openBatches')}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-2 pt-0">
                {batchesQuery.isLoading ? (
                  <div className="py-8 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" /></div>
                ) : (batchesQuery.data?.batches ?? []).length === 0 ? (
                  <p className="px-6 py-6 text-base text-muted-foreground text-center">{t('filmPrint.noBatches')}</p>
                ) : (
                  <div className="divide-y">
                    {(batchesQuery.data?.batches ?? []).map((b) => (
                      <button
                        key={b.id}
                        className="w-full px-6 py-4 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left min-h-14"
                        onClick={() => setBatchId(b.id)}
                      >
                        <Layers className="h-5 w-5 text-muted-foreground shrink-0" />
                        <span className="font-semibold text-base flex-1 tabular-nums">{t('filmPrint.batchLabel', { number: b.batch_number })}</span>
                        <Badge variant="secondary" className="text-sm shrink-0">{friendlyBatchStatus(b.status)}</Badge>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Order-number search — no batch open yet, so resolving a typed
                order number is a SERVER query (findDtfBatchesForOrder), not a
                filter over the batch list above (which is paginated). */}
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <ScanLine className="h-4 w-4" />
                  {t('filmPrint.findOrderTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <div className="flex gap-2">
                  <OrderSearchInput
                    className="flex-1"
                    value={orderSearch}
                    onChange={(v) => { setOrderSearch(v); setCrossBatchMatches(null); }}
                    onScanPayload={handleScan}
                    placeholder={t('filmPrint.orderSearchPlaceholder')}
                    ariaLabel={t('filmPrint.orderSearchPlaceholder')}
                    onSubmit={() => void handleCrossBatchSearch()}
                  />
                  <Button
                    type="button"
                    className="h-12 px-5"
                    onClick={() => void handleCrossBatchSearch()}
                    disabled={!orderSearch.trim() || crossBatchSearching}
                  >
                    {crossBatchSearching ? <Loader2 className="h-5 w-5 animate-spin" /> : t('filmPrint.findOrderButton')}
                  </Button>
                </div>
                {crossBatchMatches?.length ? (
                  // Canonical batch-list row markup (mirrors "Open Batches"
                  // above) — incl. the friendly status badge, so a cross-batch
                  // result reads exactly like every other batch row (item 8).
                  <div className="divide-y border rounded-lg">
                    {crossBatchMatches.map((b) => (
                      <button
                        key={b.batchId}
                        className="w-full px-6 py-4 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left min-h-14"
                        onClick={() => { handOffOrderSearch(orderSearch.trim()); setBatchId(b.batchId); }}
                      >
                        <Layers className="h-5 w-5 text-muted-foreground shrink-0" />
                        <span className="font-semibold text-base flex-1 tabular-nums">
                          {b.batchNumber !== null ? t('filmPrint.batchLabel', { number: b.batchNumber }) : t('filmPrint.unnumberedBatch')}
                        </span>
                        <Badge variant="secondary" className="text-sm shrink-0">{friendlyBatchStatus(b.status)}</Badge>
                      </button>
                    ))}
                  </div>
                ) : null}
              </CardContent>
            </Card>

            {/* SECONDARY — scanner-first shortcut: scan a garment to open its batch. */}
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <ScanLine className="h-4 w-4" />
                  {t('filmPrint.scanGarmentToOpen')}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-muted-foreground mb-3">{t('filmPrint.scanGarmentToOpenHint')}</p>
                <QRScannerInput onScan={handleScan} disabled={feedback.isModalOpen || mismatch.open || redoDialogGarmentId !== null || bulkRedoOpen} />
              </CardContent>
            </Card>
          </>
        )}

        {/* Batch selected — DTF garment list */}
        {batchId && garmentsQuery.isLoading && (
          <Card><CardContent className="py-16 text-center"><Loader2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-spin" /><p className="text-lg text-muted-foreground">{t('lookingUp')}</p></CardContent></Card>
        )}

        {/* No DTF units in the opened batch. NOT a dead end: name the reason
            (this batch's items are routed DTG) and hand the operator the door to
            the station that DOES have their work. Gated on !isError — a load
            FAILURE already surfaces its own error modal (below); it must
            never also render as "no DTF garments here". */}
        {batchId && !garmentsQuery.isLoading && !garmentsQuery.isError && garments.length === 0 && (
          <Card>
            <CardContent className="py-16 text-center">
              <AlertCircle
                className={cn(
                  'h-12 w-12 mx-auto mb-4',
                  emptyBatchCopy.showDtgLink
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-muted-foreground',
                )}
                aria-hidden="true"
              />
              <p
                className={cn(
                  emptyBatchCopy.detail
                    ? 'text-2xl font-semibold tabular-nums'
                    : 'text-lg text-muted-foreground',
                )}
              >
                {emptyBatchCopy.title}
              </p>
              {emptyBatchCopy.detail && (
                <p className="mt-2 text-base text-muted-foreground tabular-nums">
                  {emptyBatchCopy.detail}
                </p>
              )}
              {emptyBatchCopy.showDtgLink && (
                <Button
                  variant="outline"
                  className="mt-6 h-12 text-base gap-2"
                  onClick={() => emptyStateNavigate(printTechnologyRoute('DTG'))}
                >
                  {t('filmPrint.openDtgStation')}
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {batchId && garments.length > 0 && (
          <>
            {/* Order-number search — narrows the visible garment cards
                in-batch. Scanner-friendly (large hit target, ≥48px) but does
                NOT steal focus from the hidden barcode scanner input below. */}
            <OrderSearchInput
              value={orderSearch}
              onChange={setOrderSearch}
              onScanPayload={handleScan}
              placeholder={t('filmPrint.orderSearchPlaceholder')}
              ariaLabel={t('filmPrint.orderSearchPlaceholder')}
              onClear={() => setOrderSearch('')}
              clearAriaLabel={t('filmPrint.orderSearchClear')}
            />
            {normalizedOrderSearch && filteredGarments.length === 0 && (
              <Card>
                <CardContent className="py-10 text-center">
                  <p className="text-base text-muted-foreground">
                    {t('filmPrint.orderSearchNoMatchesInBatch', { query: orderSearch })}
                  </p>
                </CardContent>
              </Card>
            )}
            {/* Scope indicator (item 8): an in-batch search narrows the cards
                below it — state clearly how much of the batch is showing, and
                offer a one-tap way back to the full batch. */}
            {normalizedOrderSearch && filteredGarments.length > 0 && (
              <div className="flex items-center justify-between gap-3 px-1">
                <p className="text-sm text-muted-foreground tabular-nums">
                  {t('filmPrint.orderSearchScopeIndicator', {
                    n: filteredGarments.length,
                    m: garments.length,
                    query: orderSearch,
                  })}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-sm shrink-0"
                  onClick={() => setOrderSearch('')}
                >
                  {t('filmPrint.orderSearchClear')}
                </Button>
              </div>
            )}

            {/* BLU-817: films withheld because their order line was born without
                artwork. Sits immediately above the bulk actions because the count on
                "Send all to RIP (N)" is now short by these films — the operator has
                to read the reason before tapping. Deliberately NOT a card and NOT
                part of the reconciliation totals.
                No link to the Issues board: the Printer role cannot open it, so the
                station's established escalation voice ("tell your coordinator") is
                the only instruction that works for everyone who sees this screen. */}
            {hiddenForArtworkCount > 0 && (
              <p className="flex items-center gap-2 text-base font-medium text-amber-800 dark:text-amber-300">
                <AlertCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
                {t('filmPrint.hiddenForArtworkIssues', { count: hiddenForArtworkCount })}
              </p>
            )}

            {/* BATCH-FIRST primary actions: prep/mark the whole batch in ONE tap.
                Per-unit buttons remain on each card for exceptions. Disabled
                while a bulk run is in flight; progress shown on the running one. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                className="h-14 text-base gap-2"
                onClick={handleBulkPrepareAndDrop}
                disabled={bulkRun !== null || prepareableCount === 0}
              >
                {bulkRun?.kind === 'prepare' ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="tabular-nums">
                      {t('filmPrint.bulkPreparingProgress', { done: bulkRun.done, total: bulkRun.total })}
                    </span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span className="tabular-nums">
                      {t('filmPrint.bulkPrepareAll', { count: prepareableCount })}
                    </span>
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                className="h-14 text-base gap-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 dark:border-emerald-500 dark:text-emerald-300 dark:hover:bg-emerald-950/40 dark:hover:text-emerald-200"
                onClick={handleBulkMarkDone}
                disabled={bulkRun !== null || markableCount === 0}
              >
                {bulkRun?.kind === 'markDone' ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="tabular-nums">
                      {t('filmPrint.bulkMarkingProgress', { done: bulkRun.done, total: bulkRun.total })}
                    </span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    <span className="tabular-nums">
                      {t('filmPrint.bulkMarkAllDone', { count: markableCount })}
                    </span>
                  </>
                )}
              </Button>
            </div>

            {/* Batch-level "Resend all to RIP" — recovery action. Re-resolves +
                re-drops every film already sent (At RIP) in one tap; prepareFilm
                reuses each PRINTING row so nothing duplicates. For when the RIP
                folder got the wrong/stale files or the agent missed drops
                mid-batch (batch 1717). Outline like the per-card Resend; only
                shown when at least one film is resendable. */}
            {markableCount > 0 && (
              <Button
                variant="outline"
                className="w-full h-14 text-base gap-2"
                onClick={handleBulkResend}
                disabled={bulkRun !== null}
              >
                {bulkRun?.kind === 'resend' ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="tabular-nums">
                      {t('filmPrint.bulkResendProgress', { done: bulkRun.done, total: bulkRun.total })}
                    </span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span className="tabular-nums">
                      {t('filmPrint.bulkResendAll', { count: markableCount })}
                    </span>
                  </>
                )}
              </Button>
            )}

            {/* Batch-level "Redo all films" — exception action (amber = redo).
                Scraps + re-sends every film still at the station with a film sent
                (At RIP / film-printed; not pressed — see isBulkRedoable) in one
                tap. Secondary to the primary grid; only shown when at least one
                film is redoable. Opens a confirm dialog to capture ONE shared reason. */}
            {redoableCount > 0 && (
              <Button
                variant="outline"
                className="w-full h-14 text-base gap-2 border-amber-500 text-amber-700 hover:bg-amber-50 hover:text-amber-800 dark:border-amber-500 dark:text-amber-300 dark:hover:bg-amber-950/40 dark:hover:text-amber-200"
                onClick={() => setBulkRedoOpen(true)}
                disabled={bulkRun !== null}
              >
                {bulkRun?.kind === 'redo' ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="tabular-nums">
                      {t('filmPrint.bulkRedoProgress', { done: bulkRun.done, total: bulkRun.total })}
                    </span>
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-5 w-5" />
                    <span className="tabular-nums">
                      {t('filmPrint.bulkRedoAll', { count: redoableCount })}
                    </span>
                  </>
                )}
              </Button>
            )}

            {/* Hidden scanner for scan-to-advance in the detail view (Fix 7a —
                mirrors PrintStationPage's always-mounted sr-only scanner). The
                global refocus listener (un-paused unless a modal is open) routes
                keystrokes here so the operator can scan a garment QR to mark it
                done without clicking. */}
            <div className="sr-only">
              <QRScannerInput onScan={handleScan} disabled={feedback.isModalOpen || mismatch.open || redoDialogGarmentId !== null || bulkRedoOpen} />
            </div>
            <div className="space-y-3">
              {filteredGarments.map((g) => {
                const state = unitStates[g.garmentUnitId] ?? 'idle';
                const isFocused = focusGarmentId === g.garmentUnitId;
                return (
                  <FilmUnitCard
                    key={g.garmentUnitId}
                    ref={isFocused ? focusCardRef : undefined}
                    garment={g}
                    state={state}
                    isFocused={isFocused}
                    shelfSlotLabel={shelfClaims[g.garmentUnitId]}
                    needsManual={needsManualIds.has(g.garmentUnitId)}
                    retryAttempt={retryAttempts[g.garmentUnitId]}
                    escalation={escalations[g.garmentUnitId]}
                    dpi={unitDpi[g.garmentUnitId]}
                    bulkBusy={bulkRun !== null}
                    downloading={isDownloadingFilm(g.garmentUnitId)}
                    onDownload={() => void handleDownloadFilm(g.garmentUnitId, g.orderNumber)}
                    onPrepare={() => handlePrepareAndDrop(g.garmentUnitId, g.orderNumber)}
                    onMarkDone={() => handleMarkDone(g.garmentUnitId)}
                    onRedo={() => setRedoDialogGarmentId(g.garmentUnitId)}
                    onOpenImage={(kind, sourceIndex) => openLightbox(g.garmentUnitId, kind, sourceIndex)}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>

      <ScanErrorModal {...feedback.modalProps} />

      {/* BLU-1509: a DTG shirt scanned here (go to the DTG printer or do it
          DTF instead). Blocking (§6.1): the scanner is paused while open. */}
      <PrintTechnologyMismatchDialog {...mismatch.dialogProps} onSwitch={handleSwitchToDtf} />

      {/* Redo-film confirm (blocking modal §6.1 — scanner paused while open).
          Fixed film-failure reasons; distinct from the catcher garment-redo. */}
      <RedoReasonDialog
        open={redoDialogGarmentId !== null}
        onOpenChange={(open) => { if (!open) setRedoDialogGarmentId(null); }}
        onConfirm={handleRedoConfirm}
        isPending={redoFilmMutation.isPending}
        reasons={filmRedoReasons}
        copy={{
          selectTitle: t('filmPrint.redoSelectTitle'),
          confirmTitle: t('filmPrint.redoConfirmTitle'),
          confirmButtonLabel: t('filmPrint.redoConfirmButton'),
          pendingLabel: t('filmPrint.redoPending'),
          bodyText: t('filmPrint.redoConfirmBody'),
          confirmAriaLabel: (reason) => t('filmPrint.redoConfirmAria', { reason }),
          goBackAriaLabel: t('filmPrint.redoGoBackAria'),
        }}
      />

      {/* Bulk "Redo all films" confirm (blocking modal §6.1). Same reasons as the
          per-unit redo; batch-scoped copy. Progress shows on the button while the
          tolerant loop runs (the dialog closes on confirm). */}
      {/* Batch-wide image lightbox — arrows/keys flick through every artwork +
          product preview in the batch; full-size loads only while open. */}
      <ProductionImageLightbox
        open={lightbox.open}
        onOpenChange={lightbox.setOpen}
        images={lightbox.images}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
        copy={{
          title: t('filmPrint.lightbox.title'),
          description: t('filmPrint.lightbox.description'),
          previousLabel: t('preview.previousImage'),
          nextLabel: t('preview.nextImage'),
        }}
      />

      <RedoReasonDialog
        open={bulkRedoOpen}
        onOpenChange={(open) => { if (!open) setBulkRedoOpen(false); }}
        onConfirm={handleBulkRedoFilms}
        isPending={bulkRun?.kind === 'redo'}
        reasons={filmRedoReasons}
        copy={{
          selectTitle: t('filmPrint.bulkRedoSelectTitle'),
          confirmTitle: t('filmPrint.bulkRedoConfirmTitle', { count: redoableCount }),
          confirmButtonLabel: t('filmPrint.bulkRedoConfirmButton'),
          pendingLabel: t('filmPrint.bulkRedoPending'),
          bodyText: t('filmPrint.bulkRedoConfirmBody'),
          confirmAriaLabel: (reason) => t('filmPrint.redoConfirmAria', { reason }),
          goBackAriaLabel: t('filmPrint.redoGoBackAria'),
        }}
      />
    </div>
  );
}

interface OrderSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
  /** Present → renders the "×" clear button (shown only while `value` is
   *  non-empty). Absent → no clear affordance (the no-batch cross-batch
   *  search clears itself via the Find submit / batch pick instead). */
  onClear?: () => void;
  clearAriaLabel?: string;
  /** Present → Enter-to-submit fires this (self-contained `<form>`, so the
   *  caller doesn't need its own wrapping form). */
  onSubmit?: () => void;
  disabled?: boolean;
  className?: string;
  /** Scanner safety (item 8, 6-lens review): a scanner gun in keyboard-wedge
   *  mode can inject a raw `THP://...` QR payload into ANY focused text
   *  input, including this one. Route it to the station's real scan handler
   *  instead of treating "THP://..." as literal order-number text (mirrors
   *  BinManagePage.tsx's scan-vs-typed branch). */
  onScanPayload?: (payload: string) => void;
}

/** Shared order-number search field for the Film-Print station (260818-7oj
 * item 16) — the no-batch cross-batch search and the in-batch card filter
 * were two hand-rolled `<input>`s that had drifted from the `Input`
 * primitive's focus styling. Built on `<Input>`; icon + optional clear button
 * absolutely positioned over it. Station-scale text (`text-lg font-mono
 * tabular-nums`) and an `h-11 w-11` clear target mirror `IssueSearchInput.tsx`. */
function OrderSearchInput({
  value, onChange, placeholder, ariaLabel, onClear, clearAriaLabel, onSubmit, disabled, className, onScanPayload,
}: OrderSearchInputProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (next.startsWith('THP://')) {
      onChange('');
      onScanPayload?.(next);
      return;
    }
    onChange(next);
  };
  const field = (
    <div className={cn('relative', className)}>
      <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" aria-hidden="true" />
      <Input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        aria-label={ariaLabel}
        disabled={disabled}
        maxLength={50}
        className="h-12 pl-10 pr-12 text-lg font-mono tabular-nums"
      />
      {onClear && value && (
        <button
          type="button"
          onClick={onClear}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-11 w-11 flex items-center justify-center text-muted-foreground hover:text-foreground"
          aria-label={clearAriaLabel}
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
  if (!onSubmit) return field;
  return (
    <form className="contents" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
      {field}
    </form>
  );
}

interface FilmUnitCardProps {
  /** Full DTF garment row — the card reads identity (name/sku/size/color), the
   * `customPreviewUrl` artwork thumbnail, and the server `status`/`film.status`
   * that gate the redo control. */
  garment: DtfGarmentForFilm;
  state: UnitFilmState;
  /** BLU-233: ring + scroll target when this unit was handed over via a cross-tech redirect. */
  isFocused?: boolean;
  /** BLU-233 pre-printed: set when this garment's film was auto-claimed from
   * shelf stock (no Fiery drop). The string is the shelf slot label ('' when the
   * claim returned no slot). `undefined` = not claimed from shelf. */
  shelfSlotLabel?: string;
  /** True while this unit's drop failed/was-skipped in the most recent run and
   * has not yet been successfully re-sent — renders a persistent amber "Not
   * sent" chip so it can't be mistaken for a real success once the run's
   * toast summary closes (6-lens review item 6). */
  needsManual?: boolean;
  /** Phase 50.1: 1-based attempt currently in flight, when the station is
   * auto-retrying this unit (>= 2). Turns the busy button's label into
   * "Retrying (2/3)…" so the operator can see progress on the card instead of
   * watching a frozen "Preparing…". */
  retryAttempt?: number;
  /** Phase 50.1: set once the retry ladder gave up and an artwork issue was
   * filed for this unit — renders the honest amber escalation panel (which
   * REPLACES the plainer "Not sent" chip). */
  escalation?: ArtworkEscalation;
  /** Claimed source density per side from the last prepare. 300 DPI is the
   * DTF standard: any other claimed value renders a badge ("x DPI"; under
   * 300 adds "print may be blurry"). Absent until a prepare has run. */
  dpi?: { front: number | null; back: number | null };
  /** True while a batch-level bulk run is in flight — per-unit (exception)
   * buttons are disabled so they can't conflict with the bulk loop. */
  bulkBusy?: boolean;
  /** True while this unit's Download film prepare is in flight. */
  downloading?: boolean;
  /** Download the film for a manual print. Offered only on needs-manual cards. */
  onDownload?: () => void;
  onPrepare: () => void;
  onMarkDone: () => void;
  /** Open the batch-wide lightbox at this unit's artwork or product preview.
   *  `sourceIndex` is the chain position the tile ACTUALLY rendered. */
  onOpenImage?: (kind: ProductionPreviewKind, sourceIndex: number) => void;
  /** Redo the DTF film — scrap it + re-send. Offered only while the garment is
   * still in the film-print stage and its film hasn't been pressed (see the
   * `canRedo` gate); never on caught/pressed/packed units. */
  onRedo: () => void;
}

const FilmUnitCard = forwardRef<HTMLDivElement, FilmUnitCardProps>(function FilmUnitCard(
  {
    garment, state, isFocused = false, shelfSlotLabel, needsManual = false,
    retryAttempt, escalation, dpi, bulkBusy = false, downloading = false, onDownload,
    onPrepare, onMarkDone, onRedo, onOpenImage,
  },
  ref,
) {
  const { t } = useTranslation('production');
  const { organization } = useOrganization();
  // Location-scoped resolver, NOT useClientOrgLabels: that one is backed by the
  // admin-only listConnectedClients, so a Printer — who IS admitted to this
  // station — would get no roster and every client order would fall back to a
  // generic label. Same { isCrossOrg, labelFor } shape.
  const { isCrossOrg, labelFor } = useTrackerClientOrg();
  const isDone = state === 'filmPrinted';
  const isPrinting = state === 'printing';
  const isBusy = state === 'preparing';
  const elapsedSeconds = useElapsedSeconds(isBusy);
  // The SAME per-unit tile list the batch-wide flat list is built from, at card
  // resolution. See buildFilmUnitPreviewEntries: one definition, so the card
  // and the viewer can never disagree about which tiles this unit has.
  const previewEntries = useMemo(
    () => buildFilmUnitPreviewEntries(garment, 'thumb'),
    [garment],
  );

  // BLU-818: the Shopify admin deep-link, own-org rows only.
  // `isCrossOrg` returns false while `organization` is still loading, so gate on the
  // caller org being KNOWN too — otherwise a client row could flash a wrong-store link
  // for one render. `getShopifyAdminOrderUrl` returns null on any invalid input
  // (missing domain, non-positive id), so an unset store simply renders plain text.
  const isClientOrder = Boolean(organization?.id) && isCrossOrg(garment.organizationId);
  const clientOrgName = labelFor(garment.organizationId) ?? t('orders.clientOrderBadge');
  const shopifyOrderUrl = organization?.id && !isClientOrder
    ? getShopifyAdminOrderUrl(organization.shopify_domain, garment.shopifyOrderId)
    : null;
  // Redo (film_only) scraps the film + puts the garment back in a re-sendable
  // state. Available across the whole pre-catch lifecycle (LABEL_PRINTED/PICKED
  // "At RIP", FILM_PRINTED, PRESSED — right up to / including the press) BUT only
  // once a film has actually been SENT — otherwise there's nothing to redo and it
  // would be a doomed control (§10). "Film sent" = a non-defective film row exists
  // on the server, OR this session already sent/marked it (state printing/
  // filmPrinted) covering the moment before the query refetches.
  const hasSentFilm =
    (!!garment.film && garment.film.status !== 'DEFECTIVE') || state === 'printing' || state === 'filmPrinted';
  const canRedo = REDOABLE_GARMENT_STATUSES.has(garment.status) && hasSentFilm;
  // BLU-818 item 4: redo used to render NOTHING when unavailable, so an operator
  // comparing two cards could not tell why one offered it and the other didn't.
  //
  // ONLY the past-the-stage case gets a hint. The not-yet-sent case is already
  // explained by the full-width "Send to RIP" button directly above — a hint there
  // would put a line of dead text on every card at the start of every batch.
  const showRedoUnavailableHint = !isDone && !canRedo && hasSentFilm;

  return (
    <Card
      ref={ref}
      className={cn(
        'transition-all',
        garment.isRedo && 'border-l-4 border-l-amber-500',
        isDone && 'border-l-4 border-l-emerald-500',
        // Transient highlight when handed over from the DTG station (BLU-233).
        isFocused && 'ring-2 ring-indigo-500 ring-offset-2',
      )}
    >
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            {/* Side-by-side thumbnails — ARTWORK (the design going to the RIP,
                art-sm webp from OUR custom-previews bucket) + PRODUCT PREVIEW
                (the customer-facing mockup). Both are tiny lazy-loaded webps
                (never the raw multi-MB print PNG); clicking either opens the
                batch-wide lightbox at that image.

                These are the shared production preview tiles, so the checker
                backdrop, object-contain, the skeleton and the unavailable
                state all behave exactly as they do at Packing. Two separate
                ONE-SOURCE tiles: an artwork thumb that has not generated yet
                (it is made lazily by the preview-rehost cron) shows as
                unavailable rather than borrowing the product preview, because
                these two tiles answer different questions. Captions are off:
                a one-source tile's label can never change, and the pair is
                always in this order on a dense card. */}
            <ProductionPreviewStrip
              entries={previewEntries}
              subject={garment.productName || garment.sku}
              variant="compact"
              captionMode="none"
              onOpen={onOpenImage}
            />
            <div className="min-w-0 flex-1">
              {/* Order identity FIRST (§4.2/§7 — the order number is what an operator
                  matches against the physical paperwork). The card did not show it at
                  all before BLU-818: the only way to tell which order a film belonged
                  to was to open the lightbox caption.
                  Linked to the Shopify admin ONLY for the viewer's own org — a client's
                  order lives in the CLIENT's store, so the viewer's slug would build a
                  wrong-store URL. The backend already withholds `shopifyOrderId` on a
                  cross-org row, so this is defence in depth, not the only gate. */}
              {garment.orderNumber && (
                <div className="flex items-center gap-2 pb-0.5">
                  {shopifyOrderUrl ? (
                    <a
                      href={shopifyOrderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      // -m-2 p-2 grows the tap target to ≥48px without inflating the
                      // glyph (§4.3) — operators tap this with gloves.
                      className="-m-2 flex items-center gap-1.5 p-2 text-lg font-mono tabular-nums underline decoration-dotted underline-offset-4 hover:decoration-solid"
                    >
                      {formatOrderNumber(garment.orderNumber)}
                      <ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="text-lg font-mono tabular-nums">
                      {formatOrderNumber(garment.orderNumber)}
                    </span>
                  )}
                  {/* Station-scale badge, and the visible reason there is no link on a
                      client's order — never a hover tooltip (§10). */}
                  {isClientOrder && <ClientOrgBadge orgName={clientOrgName} size="station" />}
                </div>
              )}
              <p className="text-base font-semibold leading-tight">{garment.productName}</p>
              {/* Operator-critical SKU — text-sm minimum per PRODUCTION_DESIGN §4.2 (Fix 7b). */}
              <p className="font-mono text-sm text-muted-foreground">{garment.sku}</p>
              <div className="flex items-center gap-2 pt-1.5">
                {garment.size && <Badge variant="secondary" className="text-sm">{garment.size}</Badge>}
                {garment.color && <span className="text-sm">{garment.color}</span>}
                {/* DTF / REDO badges bumped text-xs → text-sm (Fix 7b, §4.2/§10). */}
                <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100 dark:bg-indigo-900/50 dark:text-indigo-200 text-sm">DTF</Badge>
                {garment.isRedo && <Badge variant="destructive" className="text-sm">REDO</Badge>}
                {/* Non-300-DPI badge (Brett 2026-08-19): 300 is the DTF
                    standard. Under 300 the print genuinely renders soft, so
                    that badge carries the "may be blurry" warning in amber;
                    over 300 is just informational. One badge per offending
                    side, prefixed with the side name only on a dual film. */}
                {([['front', dpi?.front], ['back', dpi?.back]] as const)
                  .filter((entry): entry is [('front' | 'back'), number] =>
                    typeof entry[1] === 'number' && Math.abs(entry[1] - 300) / 300 > 0.01)
                  .map(([side, value]) => {
                    const both = typeof dpi?.front === 'number' && typeof dpi?.back === 'number';
                    const sideLabel = both
                      ? `${t(side === 'front' ? 'filmPrint.sideFront' : 'filmPrint.sideBack')} ` : '';
                    return value < 300 ? (
                      <Badge
                        key={side}
                        className="bg-amber-100 text-amber-900 hover:bg-amber-100 dark:bg-amber-900/50 dark:text-amber-200 text-sm"
                      >
                        {sideLabel}{t('filmPrint.dpiBadgeBlurry', { dpi: Math.round(value) })}
                      </Badge>
                    ) : (
                      <Badge key={side} variant="secondary" className="text-sm">
                        {sideLabel}{t('filmPrint.dpiBadge', { dpi: Math.round(value) })}
                      </Badge>
                    );
                  })}
              </div>
            </div>
          </div>
          {/* Status pill */}
          {isDone ? (
            <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 shrink-0">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-semibold">{t('filmPrint.statusFilmPrinted')}</span>
            </div>
          ) : isPrinting ? (
            <Badge variant="outline" className="shrink-0 text-indigo-700 border-indigo-300 dark:text-indigo-300">{t('filmPrint.statusPrinting')}</Badge>
          ) : null}
        </div>

        {/* Nothing reached the RIP for this unit. TWO tiers, never stacked —
            a wall of amber is unreadable at arm's length:
              1. ESCALATION panel (the retry ladder gave up + an issue was
                 filed): says what happened, what the system did about it, and
                 which control to use next. Supersedes the chip.
              2. "Not sent" chip (item 6): a failed/skipped drop still lands in
                 `isPrinting` above — the SAME "At RIP" pill a real success
                 shows — so this cue is the only thing keeping it from looking
                 identical to a good drop once the run's toast closes.
            Both are suppressed on a done card: a server-confirmed film beside
            "Not sent" would contradict itself. */}
        {!isDone && escalation ? (
          <div
            role="status"
            className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
          >
            {/* Same selection as the modal (prepareUnitWithLadder's escalate branch) —
                the two must never disagree about why this unit failed. */}
            <p className="flex items-center gap-1.5 text-base font-semibold">
              <AlertCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
              {t(escalationCopyKeys(escalation).titleKey)}
            </p>
            <p className="mt-1 text-sm tabular-nums">
              {t(escalationCopyKeys(escalation).lineKey, {
                attempts: escalation.attempts,
                order: garment.orderNumber,
              })}
            </p>
          </div>
        ) : !isDone && needsManual ? (
          <div className="mt-4 flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('filmPrint.notSentChip')}
          </div>
        ) : null}

        {/* BLU-233 pre-printed: this garment's film was pulled from PRE-PRINTED
            shelf stock — no Fiery drop happened. Distinct done state: an emerald
            "pulled from shelf" caption + the shared retrieval directive pointing
            at the shelf slot (sky, glyph + text — reused from the press station). */}
        {shelfSlotLabel !== undefined && (
          <div className="mt-4 space-y-2">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
              <CheckCircle className="h-4 w-4" />
              {t('filmPrint.pulledFromShelf')}
            </p>
            {shelfSlotLabel && (
              <FilmLocationDirective
                slotLabel={shelfSlotLabel}
                storageType="shelf"
                action="retrieve"
                designHint={garment.productName}
                sizeHint={garment.size}
              />
            )}
          </div>
        )}

        {/* Actions — per-unit EXCEPTION path (a single failed/defective/redo
            unit). The batch-first bulk buttons above cover the common case; these
            are disabled while a bulk run is in flight so they can't conflict.
            Uniform h-14 targets (§4.3); "Send to RIP" is the primary (filled)
            action, "Mark Film Done" the secondary emerald-outline override. */}
        {!isDone && (
          <div className="flex flex-wrap gap-3 mt-4">
            {state === 'idle' || state === 'error' ? (
              <Button className="flex-1 h-14 text-base gap-2" onClick={onPrepare} disabled={isBusy || bulkBusy}>
                <Send className="h-5 w-5" />
                {state === 'error' ? t('filmPrint.retryPrepare') : t('filmPrint.prepareAndDrop')}
              </Button>
            ) : isBusy ? (
              /* Busy. Phase 50.1: while the station is auto-retrying this unit
                 the label counts the attempts ("Retrying (2/3)…") — the
                 operator sees the ladder working on THIS card instead of a
                 frozen "Preparing…", and the page is never blocked. */
              <Button
                className="flex-1 h-14 text-base gap-2 tabular-nums"
                disabled
                aria-live="polite"
              >
                <Loader2 className="h-5 w-5 animate-spin" />
                {retryAttempt && retryAttempt > 1
                  ? t('filmPrint.retryingProgress', { attempt: retryAttempt, total: MAX_PREPARE_ATTEMPTS })
                  : t('filmPrint.preparing')}
                {/* aria-hidden is load-bearing: this Button is already an
                    aria-live="polite" node, so an un-hidden ticking counter would make
                    a screen reader speak once a second for the whole multi-minute
                    fetch. The page-level live region (§13) stays the only speaker. */}
                {elapsedSeconds !== null && (
                  <span aria-hidden="true">{t('filmPrint.preparingElapsed', { seconds: elapsedSeconds })}</span>
                )}
              </Button>
            ) : isPrinting ? (
              /* Resend: the film row exists but the drop may have failed at the
                 agent (e.g. presigned fetch 404, batch 1688). prepareFilm is
                 idempotent for a PRINTING film (DAO reuses the row), so a
                 re-send is always a safe retry. Outline: the filled primary
                 slot stays with first-sends. */
              <Button variant="outline" className="flex-1 h-14 text-base gap-2" onClick={onPrepare} disabled={bulkBusy}>
                <Send className="h-5 w-5" />
                {t('filmPrint.resendToRip')}
              </Button>
            ) : null}
            {/* Download film: only on a card whose film never reached the RIP,
                so the operator can print it by hand. One prepare per click;
                outline (secondary), same h-14 target. */}
            {needsManual && !isBusy && onDownload && (
              <Button
                variant="outline"
                className="flex-1 h-14 text-base gap-2"
                onClick={onDownload}
                disabled={bulkBusy || downloading}
              >
                {downloading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
                {t('filmPrint.downloadFilm')}
              </Button>
            )}
            {/* Manual "mark film done" — ALWAYS exposed for any non-done state
                (Fix 5: idle / error / printing). PRODUCTION_DESIGN: ALWAYS offer a
                manual mark-done path. The backend markFilmPrinted CAS expects
                PICKED, so it's safe to surface even before "Send to RIP":
                an operator who printed/pressed out-of-band can still advance the
                unit. Secondary emerald-outline (send is the primary action). */}
            {!isBusy && (
              <Button
                variant="outline"
                className="flex-1 h-14 text-base font-semibold border-emerald-600 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 dark:border-emerald-500 dark:text-emerald-300 dark:hover:bg-emerald-950/40 dark:hover:text-emerald-200"
                onClick={onMarkDone}
                disabled={bulkBusy}
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                {t('filmPrint.markFilmDone')}
              </Button>
            )}
          </div>
        )}

        {/* Redo DTF film — scrap the film + re-send. Gated by `canRedo`: only
            while the garment is still in the film-print stage and its film isn't
            pressed (never on caught/pressed/packed units, where the server would
            reject). Amber outline (§4.1 amber = redo/waiting); the confirm dialog
            captures the reason + does the destructive scrap. */}
        {canRedo ? (
          <div className="mt-3">
            <Button
              variant="outline"
              className="w-full h-14 text-base gap-2 border-amber-500 text-amber-700 hover:bg-amber-50 hover:text-amber-800 dark:border-amber-500 dark:text-amber-300 dark:hover:bg-amber-950/40 dark:hover:text-amber-200"
              onClick={onRedo}
              disabled={bulkBusy}
            >
              <RotateCcw className="h-5 w-5" />
              {t('filmPrint.redoFilm')}
            </Button>
          </div>
        ) : showRedoUnavailableHint ? (
          /* Plain text, not a disabled button: §10 bans doomed controls, and a dead
             h-14 target is a worse lie than no target. Not a tooltip either — hover
             doesn't exist on the floor (§10). `text-base` per §4.2 (operators read at
             distance), glyph + text so colour is never the only signal (§13).
             Deliberately NOT worded "shipped": this fires on CAUGHT and PACKED units
             too, which are sitting in a bin, not on a truck. */
          <p className="mt-3 flex items-center gap-2 text-base text-muted-foreground">
            <AlertCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
            {t('filmPrint.redoUnavailablePastStage')}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
});
